import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaBarcode,
  FaCashRegister,
  FaCheck,
  FaClock,
  FaCompress,
  FaCreditCard,
  FaExpand,
  FaMobileAlt,
  FaMinus,
  FaMoneyBillWave,
  FaPlus,
  FaSearch,
  FaSignOutAlt,
  FaStore,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUser,
  FaUserMinus
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import clientesService from '../../services/clientes.service';
import configuracionService from '../../services/configuracion.service';
import productosService from '../../services/productos.service';
import ventasService from '../../services/ventas.service';
import useViewport from '../../hooks/useViewport';
import TicketVenta from '../modules/ventas/TicketVenta';
import CierreDiarioPanel from '../caja/CierreDiarioPanel';
import { canCerrarCaja, canCorregirCierreCaja, canVerHistorialCierres, canVerResumenCaja } from '../../utils/cajaPermisos';
import {
  buildCatalogIndexes,
  CATALOG_PRICE_STALE_MS,
  CATALOG_REFRESH_MS,
  getCatalogStorageKey,
  hasValidCajeroPrice,
  isCatalogStale,
  loadCatalogFromStorage,
  logCajeroPriceDebug,
  lookupProductByCode,
  lookupProductByScalePlu,
  resolveCatalogLookupKeys,
  MAX_NAME_SEARCH_RESULTS,
  NAME_SEARCH_MIN_CHARS,
  normalizeCatalogProduct,
  perfLog,
  resolveCajeroSalePrice,
  saveCatalogToStorage,
  searchProductsByName,
  summarizeCatalogPrices,
  upsertCatalogProduct
} from '../../utils/cajeroProductCache';
import { isProductoPendienteDePrecio, debeAbrirActivacionMaster, getEstadoProductoMaster } from '../../utils/masterCatalogProduct';
import ActivarProductoMasterModal from '../modules/productos/ActivarProductoMasterModal';
import AppVersionDisplay from '../common/AppVersionDisplay';
import CajeroHotkeyHint from './CajeroHotkeyHint';
import ProductoNoEncontradoModal from './ProductoNoEncontradoModal';
import PesoManualVentaModal from './PesoManualVentaModal';
import { parsePosScanCommand, POS_SCAN_COMMAND_LABELS } from '../../utils/posScanCommands';
import { parseScaleBarcode } from '../../utils/balanza';
import { isCodigoInterno } from '../../utils/codigoInterno';
import { isProductoPesoManual, lineaCarritoTotal, formatMonedaPeso, TIPO_VENTA_PESO_MANUAL } from '../../utils/pesoManualVenta';
import { parseListasPreciosFromEmpresaConfig } from '../../utils/listasPreciosLabels';
import { setCajeroVentaEnProceso } from '../../utils/criticalOperationGuard';
import { BILLETERA_VIRTUAL_LABEL } from '../../utils/metodoPagoLabels';
import { computeVentaPago, isMetodoCredito } from '../../utils/ventaPagoCompute';
import {
  parseLimiteCreditoCliente,
  calcularDisponibleCredito,
  evaluarLimiteCreditoVenta
} from '../../utils/clienteLimiteCredito';
import RegistrarPagoDialog from '../modules/ventas/RegistrarPagoDialog';
import { getUserFullName } from '../../utils/userIdentity';
import UserAvatar from '../common/UserAvatar';
import {
  buildFilasCuentaCorriente,
  extractVentasCliente
} from '../../utils/cuentaCorriente';
import { normalizarDeudaVencidaDias } from '../../utils/dashboardDeuda';

const formatMoneda = (value) => `$${(parseFloat(value || 0)).toFixed(2)}`;
const formatLineaMoneda = (item, value) =>
  item?.tipoVenta === TIPO_VENTA_PESO_MANUAL ? formatMonedaPeso(value) : formatMoneda(value);
const FECHA_DIA_NEGOCIO_TZ = 'America/Argentina/Buenos_Aires';
const fechaDiaNegocioLocal = (date = new Date()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: FECHA_DIA_NEGOCIO_TZ }).format(date);
const crearTransactionIdVenta = () =>
  `pos-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
const OFFLINE_SALES_KEY = 'mobile_pos_offline_sales_v1';
const POS_SESSION_KEY = 'mobile_pos_session_v1';
const DEFAULT_CAJA_APK_URL = 'https://nexopos-dc.web.app/app-caja.apk';
const DEFAULT_CAJA_APK_VERSION_META_URL = 'https://nexopos-dc.web.app/app-caja-version.json';

const normalizeExternalUrl = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (/^\/\//.test(value)) return `https:${value}`;
  return `https://${value}`;
};

const toPositiveInt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
};

const getApkVersionMetaUrl = (apkUrl) => {
  try {
    const parsed = new URL(apkUrl);
    const parts = parsed.pathname.split('/');
    parts[parts.length - 1] = 'app-caja-version.json';
    parsed.pathname = parts.join('/');
    return parsed.toString();
  } catch {
    return DEFAULT_CAJA_APK_VERSION_META_URL;
  }
};

const readNativeInstalledVersion = () => {
  try {
    const bridge = window?.NexoAndroid;
    if (!bridge) return { code: 0, name: '' };
    const code =
      typeof bridge.getInstalledVersionCode === 'function'
        ? toPositiveInt(bridge.getInstalledVersionCode())
        : 0;
    const name =
      typeof bridge.getInstalledVersionName === 'function'
        ? String(bridge.getInstalledVersionName() || '').trim()
        : '';
    return { code, name };
  } catch {
    return { code: 0, name: '' };
  }
};

const fetchServerApkVersion = async (apkUrl) => {
  const metaUrl = getApkVersionMetaUrl(apkUrl);
  const response = await fetch(metaUrl, { cache: 'no-store' });
  if (!response.ok) return { code: 0, name: '', metaUrl };
  const body = await response.json().catch(() => ({}));
  return {
    code: toPositiveInt(body?.versionCode || body?.version_code),
    name: String(body?.versionName || body?.version_name || '').trim(),
    metaUrl
  };
};

const isNativeCajaApk = () => {
  try {
    return Boolean(
      window?.NexoAndroid &&
      typeof window.NexoAndroid.getInstalledVersionCode === 'function'
    );
  } catch {
    return false;
  }
};

const abrirUrlApkSegura = (apkUrl) => {
  try {
    const bridge = window?.NexoAndroid;
    if (bridge && typeof bridge.openExternalUrlInChrome === 'function') {
      bridge.openExternalUrlInChrome(apkUrl);
      return true;
    }
  } catch (error) {
    console.warn('[MOBILE POS] openExternalUrlInChrome falló:', error);
  }
  try {
    window.open(apkUrl, '_blank', 'noopener,noreferrer');
    return true;
  } catch {
    return false;
  }
};

const recargarWebCajero = () => {
  try {
    const url = `${window.location.origin}/cajero?nexoFresh=${Date.now()}`;
    window.location.replace(url);
    return true;
  } catch (error) {
    console.warn('[MOBILE POS] replace /cajero falló:', error);
  }
  try {
    window.location.reload();
    return true;
  } catch (error) {
    console.warn('[MOBILE POS] reload falló:', error);
    return false;
  }
};

/** Contado en cajero; crédito solo con cliente (mismo motor que PuntoVenta + ventaPagoCompute). */
const METODOS_PAGO_CAJERO_CONTADO = ['efectivo', 'tarjeta', 'MercadoPago', 'transferencia'];
const METODO_PAGO_CAJERO_DEFAULT = 'efectivo';
const METODO_CUENTA_CORRIENTE_CAJERO = 'credito';

const normalizarMetodoPagoCajero = (raw) => {
  const s = String(raw ?? 'efectivo')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!s || s === 'cash') return 'efectivo';
  if (['tarjeta', 'card', 'debito', 'credito_tarjeta'].includes(s)) return 'tarjeta';
  if (['mercadopago', 'mercado pago', 'mercado_pago', 'mp', 'billetera', 'wallet', 'qr'].includes(s)) {
    return 'MercadoPago';
  }
  if (['transferencia', 'transf', 'banco', 'cbu', 'alias'].includes(s)) return 'transferencia';
  if (['credito', 'cuenta corriente', 'cuenta_corriente', 'ctacte', 'fiado'].includes(s)) return 'credito';
  if (METODOS_PAGO_CAJERO_CONTADO.includes(String(raw ?? '').trim())) {
    return String(raw).trim();
  }
  return METODO_PAGO_CAJERO_DEFAULT;
};

const esMetodoPagoCajeroContadoPermitido = (metodo) =>
  METODOS_PAGO_CAJERO_CONTADO.includes(normalizarMetodoPagoCajero(metodo));

const esMetodoPagoCajeroPermitido = (metodo, clienteId = null) => {
  const normalizado = normalizarMetodoPagoCajero(metodo);
  if (esMetodoPagoCajeroContadoPermitido(normalizado)) return true;
  if (normalizado === METODO_CUENTA_CORRIENTE_CAJERO && clienteId) return true;
  return false;
};

const esMetodoEfectivoCajero = (metodo) => normalizarMetodoPagoCajero(metodo) === 'efectivo';

/** Cierre diario de caja — backend /caja/cierre-dia */
const ENABLE_CAJERO_CIERRE_DIARIO = true;

const obtenerStockSucursal = (producto) => (
  parseFloat(
    producto?.stock_sucursal ??
    producto?.stock_actual ??
    producto?.cantidad ??
    producto?.stock ??
    0
  ) || 0
);

const CONSUMIDOR_FINAL = {
  id: null,
  nombre: 'Consumidor final',
  apellido: '',
  nombre_completo: 'Consumidor final'
};

const nombreCliente = (cliente) => (
  cliente?.nombre_completo ||
  `${cliente?.nombre || ''} ${cliente?.apellido || ''}`.trim() ||
  'Consumidor final'
);

const formatearFecha = (value) => {
  if (!value) return 'Sin fecha';
  const fecha = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(fecha.getTime())) return 'Sin fecha';
  return fecha.toLocaleDateString('es-AR');
};

/** Saldo oficial GET /clientes/:id/saldo — única fuente para alerta inicial. */
const clienteTieneDeudaOficial = (saldoInfo) => (Number(saldoInfo?.saldo_actual) || 0) > 0.01;

const filaComprobanteAVentaCajero = (fila, cliente) => ({
  id: fila.id,
  numero: fila.numero,
  saldo_pendiente: Number(fila.saldo) || 0,
  total: Number(fila.total) || Number(fila.saldo) || 0,
  cliente_info: {
    nombre_completo: nombreCliente(cliente)
  }
});

const MENSAJE_SALDO_SIN_COMPROBANTES_CAJA =
  'El cliente posee saldo pendiente, pero no existen comprobantes disponibles para cobrar desde caja.';

const MobilePuntoVenta = () => {
  const {
    currentUser,
    logout,
    orgId,
    sucursalSeleccionada,
    sucursalesDisponibles,
    hasPermission
  } = useAuth();
  const navigate = useNavigate();

  const [activacionMaster, setActivacionMaster] = useState({ open: false, producto: null });
  const [productoNoEncontrado, setProductoNoEncontrado] = useState({ open: false, codigo: '' });
  const [pesoManualVenta, setPesoManualVenta] = useState({ open: false, producto: null });
  const agregarProductoRef = useRef(null);
  const abrirPesoManualRef = useRef(null);

  const bloquearOActivarProducto = (producto) => {
    if (debeAbrirActivacionMaster(producto)) {
      searchInputRef.current?.blur();
      setActivacionMaster({ open: true, producto });
      return true;
    }
    if (isProductoPendienteDePrecio(producto)) {
      notifyProductoPendientePrecio(producto);
      return true;
    }
    return false;
  };

  const notifyProductoPendientePrecio = (producto) => {
    toast.error(
      ({ closeToast }) => (
        <div>
          <p className="font-semibold">Producto pendiente de precio</p>
          <p className="text-sm mt-1">{producto?.nombre}</p>
          <button
            type="button"
            className="mt-2 rounded bg-amber-600 px-3 py-1 text-sm text-white hover:bg-amber-700"
            onClick={() => {
              closeToast?.();
              navigate(`/productos/editar/${producto.id}`, { state: { focusPrecio: true } });
            }}
          >
            Cargar precio
          </button>
        </div>
      ),
      { autoClose: 8000 }
    );
  };

  const searchInputRef = useRef(null);
  const ventaEnProcesoRef = useRef(false);

  useEffect(() => () => setCajeroVentaEnProceso(false), []);
  const ventaTransactionIdRef = useRef(null);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [montoRecibido, setMontoRecibido] = useState('');
  const [sucursalVenta, setSucursalVenta] = useState('');
  const [loadingBusqueda, setLoadingBusqueda] = useState(false);
  const [procesandoVenta, setProcesandoVenta] = useState(false);
  const [ultimaVenta, setUltimaVenta] = useState(null);
  const [configTicket, setConfigTicket] = useState(null);
  const [ticketPendiente, setTicketPendiente] = useState(null);
  /** Unifica impresión con TicketVenta (58mm en caja). null | { venta, autoPrint } */
  const [ticketVentaPayload, setTicketVentaPayload] = useState(null);
  const [mostrarCarritoCompleto, setMostrarCarritoCompleto] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(CONSUMIDOR_FINAL);
  const [mostrarClientes, setMostrarClientes] = useState(false);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [filasComprobantesPagables, setFilasComprobantesPagables] = useState([]);
  const [saldoOficialCliente, setSaldoOficialCliente] = useState(null);
  const [mostrarDeudaCliente, setMostrarDeudaCliente] = useState(false);
  const [modalAlertaDeudaInicial, setModalAlertaDeudaInicial] = useState(false);
  const [pagoDeudaDialog, setPagoDeudaDialog] = useState({ open: false, venta: null });
  const deudaAlertaContinuadaRef = useRef(null);
  const [comprobanteDeuda, setComprobanteDeuda] = useState(null);
  const [montoPagoDeuda, setMontoPagoDeuda] = useState('');
  const [medioPagoDeuda, setMedioPagoDeuda] = useState('efectivo');
  const [pagandoDeuda, setPagandoDeuda] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [actualizandoApp, setActualizandoApp] = useState(false);
  const [pendingSalesCount, setPendingSalesCount] = useState(0);
  const [catalogReady, setCatalogReady] = useState(false);
  const [catalogCount, setCatalogCount] = useState(0);
  const [catalogUsingSaved, setCatalogUsingSaved] = useState(false);
  const [catalogRefreshing, setCatalogRefreshing] = useState(false);
  const [apkUpdateBanner, setApkUpdateBanner] = useState(null);
  const [apkBannerDismissed, setApkBannerDismissed] = useState(false);
  const [mostrarModalCierreCaja, setMostrarModalCierreCaja] = useState(false);
  const [modalStockNegativo, setModalStockNegativo] = useState(false);
  const [modalCreditoExcedido, setModalCreditoExcedido] = useState(null);
  const creditoExcedidoAceptadoRef = useRef(false);
  const [stockNegConfirmChecked, setStockNegConfirmChecked] = useState(false);
  const [stockNegAdminEmail, setStockNegAdminEmail] = useState('');
  const [stockNegAdminPassword, setStockNegAdminPassword] = useState('');
  const [stockNegMotivo, setStockNegMotivo] = useState('');
  const [lineasStockNegativo, setLineasStockNegativo] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hotkeyStateRef = useRef(null);
  const pendingCheckoutRef = useRef(null);
  const catalogIndexesRef = useRef(null);
  const catalogLastRefreshRef = useRef(0);
  const catalogRefreshInFlightRef = useRef(false);
  const restoredSessionRef = useRef(false);
  const viewport = useViewport();
  const visibleHeight = Math.max(
    320,
    Number(viewport.visualHeight || viewport.height || 700)
  );

  /** Monitor táctil / ordenador POS apaisado: ≥900px y ancho > alto (sin scroll de página en shell). */
  const landscapeTablet =
    viewport.width >= 900 &&
    viewport.width > visibleHeight &&
    visibleHeight >= 260;
  const splitLandscapeLayout = landscapeTablet;

  const compact =
    !splitLandscapeLayout &&
    (viewport.width < 390 ||
      visibleHeight < 760);

  /** Foco estable en buscador/lector sin pelear modales ni el campo "Dinero recibido" (data-pos-busqueda-pausada). */
  const enfocarBusquedaPOS = useCallback(() => {
    if (activacionMaster.open || productoNoEncontrado.open || pesoManualVenta.open) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const el = searchInputRef.current;
        if (!el || el.disabled) return;
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      });
    });
  }, [activacionMaster.open, productoNoEncontrado.open, pesoManualVenta.open]);

  /** Fullscreen web: solo navegador (en Android nativo la app ya es pantalla completa). */
  const fullscreenDisponible =
    typeof document !== 'undefined' &&
    typeof document.documentElement?.requestFullscreen === 'function' &&
    !isNativeCajaApk();

  const alternarPantallaCompleta = useCallback(() => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      } else {
        document.exitFullscreen?.().catch(() => {});
      }
    } catch {
      // Navegador sin soporte o bloqueo de permisos: se ignora.
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    onFsChange();
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const overlayFocoBusquedaAbiertoRef = useRef(false);
  useEffect(() => {
    const open = Boolean(
      activacionMaster.open ||
        productoNoEncontrado.open ||
        pesoManualVenta.open ||
        mostrarClientes ||
        mostrarDeudaCliente ||
        modalAlertaDeudaInicial ||
        pagoDeudaDialog.open ||
        mostrarCarritoCompleto ||
        mostrarModalCierreCaja ||
        ticketPendiente ||
        ticketVentaPayload?.venta
    );
    const wasOpen = overlayFocoBusquedaAbiertoRef.current;
    overlayFocoBusquedaAbiertoRef.current = open;
    if (wasOpen && !open) {
      enfocarBusquedaPOS();
    }
  }, [
    activacionMaster.open,
    productoNoEncontrado.open,
    pesoManualVenta.open,
    mostrarClientes,
    mostrarDeudaCliente,
    modalAlertaDeudaInicial,
    pagoDeudaDialog.open,
    mostrarCarritoCompleto,
    mostrarModalCierreCaja,
    ticketPendiente,
    ticketVentaPayload?.venta,
    enfocarBusquedaPOS
  ]);

  const cajaModulos = configTicket?.caja_modulos || {};
  const posPoliticas = useMemo(() => ({
    permitir_venta_stock_negativo: configTicket?.pos_politicas?.permitir_venta_stock_negativo === true,
    requiere_autorizacion_stock_negativo: configTicket?.pos_politicas?.requiere_autorizacion_stock_negativo !== false,
    umbral_diferencia_cierre_admin: Number(configTicket?.pos_politicas?.umbral_diferencia_cierre_admin ?? 500) || 500,
    redondeo_peso_manual: Number(configTicket?.pos_politicas?.redondeo_peso_manual ?? 0) || 0,
    permitir_venta_peso_manual: configTicket?.pos_politicas?.permitir_venta_peso_manual !== false,
    permitir_modificar_importe_peso_manual: configTicket?.pos_politicas?.permitir_modificar_importe_peso_manual === true,
    requiere_autorizacion_modificar_importe_peso: configTicket?.pos_politicas?.requiere_autorizacion_modificar_importe_peso !== false
  }), [configTicket]);

  const listaPrecioCajero = useMemo(() => (
    parseListasPreciosFromEmpresaConfig(configTicket || {}).lista_precio_punto_venta_default
  ), [configTicket]);

  const normalizeForCajero = useCallback(
    (producto) => normalizeCatalogProduct(producto, sucursalVenta, listaPrecioCajero),
    [sucursalVenta, listaPrecioCajero]
  );

  const upsertForCajero = useCallback(
    (indexes, rawProduct) => upsertCatalogProduct(indexes, rawProduct, sucursalVenta, listaPrecioCajero),
    [sucursalVenta, listaPrecioCajero]
  );

  const obtenerPrecioVentaCajero = useCallback((producto) => {
    const resolved = resolveCajeroSalePrice(producto, listaPrecioCajero);
    logCajeroPriceDebug('obtenerPrecioVenta', producto, resolved);
    return resolved;
  }, [listaPrecioCajero]);
  const cajaClientesHabilitado = cajaModulos.clientes !== false;
  const cajaAlertaDeudasHabilitada = cajaModulos.alerta_deudas !== false;
  const cajaPagoDeudasHabilitado = cajaModulos.pago_deudas !== false;
  const cajaVerComprobanteHabilitado = cajaModulos.ver_comprobante_deuda !== false;

  const cerrarSesion = async () => {
    try {
      clearPosSession();
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('[MOBILE POS] Error al cerrar sesión:', error);
      toast.error('No se pudo cerrar la sesión');
    }
  };

  const readOfflineQueue = useCallback(() => {
    try {
      const raw = localStorage.getItem(OFFLINE_SALES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const loadPosSession = useCallback(() => {
    try {
      const raw = localStorage.getItem(POS_SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const savePosSession = useCallback((snapshot) => {
    try {
      localStorage.setItem(POS_SESSION_KEY, JSON.stringify(snapshot || {}));
    } catch {
      // Ignorar errores de storage en webviews restringidos
    }
  }, []);

  const clearPosSession = useCallback(() => {
    try {
      localStorage.removeItem(POS_SESSION_KEY);
    } catch {
      // Ignorar errores de storage en webviews restringidos
    }
  }, []);

  const writeOfflineQueue = useCallback((items) => {
    const normalized = Array.isArray(items) ? items : [];
    localStorage.setItem(OFFLINE_SALES_KEY, JSON.stringify(normalized));
    setPendingSalesCount(normalized.length);
  }, []);

  const detectLegacyCreditOfflineQueue = useCallback(() => {
    const queue = readOfflineQueue();
    const legacy = queue.filter(
      (item) => normalizarMetodoPagoCajero(item?.venta?.metodo_pago) === 'credito'
    );
    return { count: legacy.length, items: legacy };
  }, [readOfflineQueue]);

  const enqueueOfflineSale = useCallback((payload) => {
    const metodoOffline = normalizarMetodoPagoCajero(payload?.venta?.metodo_pago);
    if (metodoOffline === 'credito') {
      throw new Error('Las ventas a crédito no se guardan offline en cajero. Usá Punto de Venta.');
    }
    const queue = readOfflineQueue();
    queue.push({
      ...payload,
      offlineSaleId: `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: new Date().toISOString()
    });
    writeOfflineQueue(queue);
  }, [readOfflineQueue, writeOfflineQueue]);

  const syncOfflineSales = useCallback(async () => {
    if (!navigator.onLine) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;

    let remaining = [...queue];
    let synced = 0;

    for (const item of queue) {
      const metodoSync = normalizarMetodoPagoCajero(item?.venta?.metodo_pago);
      if (metodoSync === 'credito') {
        console.warn('[MOBILE POS] Venta offline con crédito omitida:', item.offlineSaleId);
        toast.error(
          'Hay una venta pendiente con método crédito. Eliminá ese pendiente o registrála desde Punto de Venta.',
          { autoClose: 8000 }
        );
        continue;
      }
      try {
        await ventasService.crear(item.venta, item.detalles, item.sucursalVenta);
        remaining = remaining.filter((r) => r.offlineSaleId !== item.offlineSaleId);
        synced += 1;
      } catch (error) {
        console.warn('[MOBILE POS] No se pudo sincronizar pendiente:', item.offlineSaleId, error?.message);
      }
    }

    writeOfflineQueue(remaining);
    if (synced > 0) toast.success(`Se sincronizaron ${synced} venta(s) pendiente(s).`);
  }, [readOfflineQueue, writeOfflineQueue]);

  const actualizarAppCaja = async () => {
    let willReload = false;
    try {
      setActualizandoApp(true);
      await cargarConfigTicket().catch(() => null);

      const envCajaApkUrl = (process.env.REACT_APP_CAJA_APK_URL || '').trim();
      const apkUrl = normalizeExternalUrl(
        envCajaApkUrl ||
        DEFAULT_CAJA_APK_URL ||
        configTicket?.caja_apk_url
      );

      const nativeApk = isNativeCajaApk();
      if (nativeApk && apkUrl) {
        try {
          const installedVersion = readNativeInstalledVersion();
          const serverVersion = await fetchServerApkVersion(apkUrl);
          if (serverVersion.code > 0 && installedVersion.code > 0 && serverVersion.code > installedVersion.code) {
            const opened = abrirUrlApkSegura(apkUrl);
            if (opened) {
              toast.info(
                `Nueva versión v${serverVersion.name || serverVersion.code} disponible. Descarga iniciada en el navegador.`
              );
              setApkUpdateBanner(null);
              return;
            }
            toast.warning('No se pudo abrir la descarga. Recargando interfaz web…');
          } else if (serverVersion.code > 0 && installedVersion.code > 0 && installedVersion.code >= serverVersion.code) {
            toast.success(`La app ya está actualizada (v${installedVersion.name || installedVersion.code}). Recargando interfaz…`);
          }
        } catch (error) {
          console.warn('[MOBILE POS] Verificación APK:', error);
          toast.info('Recargando interfaz web…');
        }
      } else {
        toast.info('Recargando interfaz web…');
      }

      willReload = recargarWebCajero();
      if (!willReload) {
        toast.error('No se pudo recargar. Reiniciá la app manualmente.');
      }
    } catch (error) {
      console.error('[MOBILE POS] Error al actualizar caja:', error);
      toast.error('No se pudo actualizar. Probá de nuevo.');
    } finally {
      setActualizandoApp(false);
      if (!willReload) {
        enfocarBusquedaPOS();
      }
    }
  };

  const abrirModalCierreDesdeMostrador = useCallback(() => {
    if (!ENABLE_CAJERO_CIERRE_DIARIO) {
      toast.info('Cierre diario próximamente disponible.');
      return;
    }
    if (!orgId) {
      toast.error('Seleccioná una organización para cerrar caja');
      return;
    }
    if (!canVerResumenCaja(hasPermission)) {
      toast.warning('Sin permiso para ver cierre de caja');
      return;
    }
    if (!sucursalVenta) {
      toast.error('Seleccioná una sucursal para cerrar caja');
      return;
    }
    setMostrarModalCierreCaja(true);
  }, [orgId, sucursalVenta, hasPermission]);

  const cargarConfigTicket = useCallback(async () => {
    try {
      const config = await configuracionService.obtener();
      setConfigTicket(config);
      return config;
    } catch (error) {
      console.warn('[MOBILE POS] No se pudo cargar configuración de ticket:', error.message);
      const configPorDefecto = configuracionService.obtenerConfiguracionPorDefecto();
      setConfigTicket(configPorDefecto);
      return configPorDefecto;
    }
  }, []);

  useEffect(() => {
    const sucursalReal = sucursalesDisponibles?.find((sucursal) => (
      sucursal?.id && sucursal.id !== 'sucursal-principal'
    ));
    const sucursalInicial =
      sucursalReal?.id ||
      (sucursalSeleccionada?.id !== 'sucursal-principal' ? sucursalSeleccionada?.id : '') ||
      sucursalesDisponibles?.[0]?.id ||
      '';
    const sucursalActualExiste = sucursalesDisponibles?.some((sucursal) => sucursal.id === sucursalVenta);

    if (
      sucursalInicial &&
      (!sucursalVenta || sucursalVenta === 'sucursal-principal' || (sucursalesDisponibles?.length > 0 && !sucursalActualExiste))
    ) {
      setSucursalVenta(sucursalInicial);
      setResultados([]);
    }
  }, [sucursalSeleccionada, sucursalVenta, sucursalesDisponibles]);

  useEffect(() => {
    enfocarBusquedaPOS();
  }, [enfocarBusquedaPOS]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (procesandoVenta) return;
      if (activacionMaster.open) return;
      if (
        mostrarClientes ||
        mostrarDeudaCliente ||
        modalAlertaDeudaInicial ||
        pagoDeudaDialog.open ||
        mostrarCarritoCompleto ||
        mostrarModalCierreCaja ||
        ticketPendiente ||
        ticketVentaPayload?.venta
      ) {
        return;
      }
      const elBusq = searchInputRef.current;
      if (!elBusq || elBusq.disabled) return;
      const active = document.activeElement;
      if (active === elBusq) return;
      if (active?.closest?.('[data-pos-busqueda-pausada="1"]')) return;
      if (active?.closest?.('[data-activacion-master-modal="1"]')) return;
      if (active?.closest?.('[data-pos-checkout="1"]')) return;

      enfocarBusquedaPOS();
    }, 380);
    return () => window.clearInterval(id);
  }, [
    procesandoVenta,
    activacionMaster.open,
    mostrarClientes,
    mostrarDeudaCliente,
    modalAlertaDeudaInicial,
    pagoDeudaDialog.open,
    mostrarCarritoCompleto,
    mostrarModalCierreCaja,
    ticketPendiente,
    ticketVentaPayload?.venta,
    enfocarBusquedaPOS
  ]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (procesandoVenta) return;
      if (activacionMaster.open) return;
      if (
        mostrarClientes ||
        mostrarDeudaCliente ||
        modalAlertaDeudaInicial ||
        pagoDeudaDialog.open ||
        mostrarCarritoCompleto ||
        mostrarModalCierreCaja ||
        ticketPendiente ||
        ticketVentaPayload?.venta
      ) {
        return;
      }
      const active = document.activeElement;
      if (active?.closest?.('[data-pos-busqueda-pausada="1"]')) return;
      if (active?.closest?.('[data-activacion-master-modal="1"]')) return;
      if (active?.closest?.('[data-pos-checkout="1"]')) return;
      enfocarBusquedaPOS();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [
    procesandoVenta,
    activacionMaster.open,
    mostrarClientes,
    mostrarDeudaCliente,
    modalAlertaDeudaInicial,
    pagoDeudaDialog.open,
    mostrarCarritoCompleto,
    mostrarModalCierreCaja,
    ticketPendiente,
    ticketVentaPayload?.venta,
    enfocarBusquedaPOS
  ]);

  useEffect(() => {
    if (restoredSessionRef.current) return;
    const session = loadPosSession();
    if (!session) {
      restoredSessionRef.current = true;
      return;
    }

    if (Array.isArray(session.carrito)) {
      setCarrito(session.carrito);
    }
    if (typeof session.metodoPago === 'string' && session.metodoPago) {
      const normalizedPay = normalizarMetodoPagoCajero(session.metodoPago);
      const clienteIdSesion = session.clienteSeleccionado?.id ?? null;
      setMetodoPago(
        esMetodoPagoCajeroPermitido(normalizedPay, clienteIdSesion)
          ? normalizedPay
          : METODO_PAGO_CAJERO_DEFAULT
      );
    }
    if (typeof session.montoRecibido === 'string') {
      setMontoRecibido(session.montoRecibido);
    }
    if (typeof session.sucursalVenta === 'string') {
      setSucursalVenta(session.sucursalVenta);
    }
    if (session.clienteSeleccionado && typeof session.clienteSeleccionado === 'object') {
      setClienteSeleccionado(session.clienteSeleccionado);
    }
    restoredSessionRef.current = true;
  }, [loadPosSession]);

  useEffect(() => {
    if (!restoredSessionRef.current) return;
    savePosSession({
      carrito,
      metodoPago,
      montoRecibido,
      sucursalVenta,
      clienteSeleccionado
    });
  }, [carrito, metodoPago, montoRecibido, sucursalVenta, clienteSeleccionado, savePosSession]);

  useEffect(() => {
    setPendingSalesCount(readOfflineQueue().length);
    const legacy = detectLegacyCreditOfflineQueue();
    if (legacy.count > 0) {
      console.warn(
        '[MOBILE POS] Cola offline legacy con crédito (no sincronizable):',
        legacy.items.map((i) => i.offlineSaleId)
      );
      toast.warning(
        `Hay ${legacy.count} venta(s) offline antigua(s) con crédito que no se pueden sincronizar en cajero. Eliminá ese pendiente o registrá la venta desde Punto de Venta.`,
        { autoClose: 12000 }
      );
    }
  }, [readOfflineQueue, detectLegacyCreditOfflineQueue]);

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      syncOfflineSales();
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [syncOfflineSales]);

  useEffect(() => {
    if (isOnline) syncOfflineSales();
  }, [isOnline, syncOfflineSales]);

  useEffect(() => {
    cargarConfigTicket();
  }, [cargarConfigTicket]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!isNativeCajaApk()) return;
        const apkUrl = normalizeExternalUrl(
          (process.env.REACT_APP_CAJA_APK_URL || '').trim() || DEFAULT_CAJA_APK_URL
        );
        const installed = readNativeInstalledVersion();
        const server = await fetchServerApkVersion(apkUrl);
        if (cancelled || !server.code) return;
        if (installed.code > 0 && server.code > installed.code) {
          setApkUpdateBanner({
            apkUrl,
            serverCode: server.code,
            serverName: server.name || String(server.code)
          });
        }
      } catch (error) {
        console.warn('[MOBILE POS] Auto-check versión APK:', error?.message || error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyCatalog = useCallback((products, { fromCache = false } = {}) => {
    const list = (Array.isArray(products) ? products : []).map((p) => (
      normalizeForCajero(p)
    ));
    catalogIndexesRef.current = buildCatalogIndexes(list);
    setCatalogCount(list.length);
    setCatalogReady(list.length > 0);
    setCatalogUsingSaved(fromCache);
    if (!fromCache) {
      catalogLastRefreshRef.current = Date.now();
    }
  }, [sucursalVenta, listaPrecioCajero]);

  useEffect(() => {
    if (!catalogIndexesRef.current?.byId?.size) return;
    const products = Array.from(catalogIndexesRef.current.byId.values());
    applyCatalog(products, { fromCache: true });
  }, [listaPrecioCajero, applyCatalog]);

  const refreshCatalogoCajero = useCallback(async ({ force = false, silent = false } = {}) => {
    if (!sucursalVenta) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      if (orgId && !catalogIndexesRef.current?.count) {
        const cached = loadCatalogFromStorage(getCatalogStorageKey(orgId, sucursalVenta));
        if (cached?.products?.length) {
          applyCatalog(cached.products, { fromCache: true });
          catalogLastRefreshRef.current = cached.fetchedAt || 0;
        }
      }
      return;
    }
    if (catalogRefreshInFlightRef.current && !force) return;

    if (force) {
      catalogRefreshInFlightRef.current = false;
      if (orgId) {
        try {
          localStorage.removeItem(getCatalogStorageKey(orgId, sucursalVenta));
        } catch {
          // storage bloqueado
        }
      }
    }

    catalogRefreshInFlightRef.current = true;
    if (!silent) setCatalogRefreshing(true);

    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;

    try {
      const productos = await productosService.buscarConStockPorSucursal('', sucursalVenta);
      const list = Array.isArray(productos) ? productos : [];
      const normalized = list.map((p) => normalizeForCajero(p));

      if (orgId) {
        saveCatalogToStorage(getCatalogStorageKey(orgId, sucursalVenta), normalized);
      }

      applyCatalog(normalized, { fromCache: false });
      setCatalogUsingSaved(false);
      perfLog('refresh-api', (typeof performance !== 'undefined' ? performance.now() : 0) - t0, {
        count: normalized.length
      });

      if (!silent) {
        const priceSummary = summarizeCatalogPrices(normalized);
        if (normalized.length > 0) {
          toast.success(`Catálogo actualizado: ${normalized.length} productos`);
          if (priceSummary.withoutPrice > 0) {
            toast.warning(`${priceSummary.withoutPrice} producto(s) sin precio configurado`);
          }
          const sinStock = normalized.filter((p) => Number(obtenerStockSucursal(p) || 0) <= 0).length;
          if (sinStock === normalized.length) {
            toast.info('Stock 0 en esta sucursal. Transferí producto desde otra para vender.');
          }
        } else {
          toast.warning('No hay productos en el catálogo de la empresa');
        }
      }
    } catch (error) {
      console.warn('[MOBILE POS] No se pudo refrescar catálogo:', error?.message || error);
      if (!catalogIndexesRef.current?.count) {
        toast.error('No se pudo cargar el catálogo');
      } else if (!silent) {
        toast.warning('No se pudo actualizar catálogo. Usando catálogo guardado.');
      }
      setCatalogUsingSaved(true);
    } finally {
      catalogRefreshInFlightRef.current = false;
      setCatalogRefreshing(false);
      if (!silent) {
        enfocarBusquedaPOS();
      }
    }
  }, [applyCatalog, orgId, sucursalVenta, enfocarBusquedaPOS, normalizeForCajero]);

  const buscarProductosApi = useCallback(async (termino) => {
    const texto = termino.trim();
    if (!texto || !sucursalVenta) return;

    try {
      setLoadingBusqueda(true);
      const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
      const productos = await productosService.buscarConStockPorSucursal(texto, sucursalVenta);
      perfLog('nombre-api', (typeof performance !== 'undefined' ? performance.now() : 0) - t0, {
        hits: Array.isArray(productos) ? productos.length : 0
      });
      setResultados(
        Array.isArray(productos)
          ? productos.slice(0, MAX_NAME_SEARCH_RESULTS).map((p) => normalizeForCajero(p))
          : []
      );
    } catch (error) {
      console.error('[MOBILE POS] Error al buscar productos:', error);
      if (!catalogIndexesRef.current?.count) {
        toast.error('No se pudieron buscar productos');
      }
    } finally {
      setLoadingBusqueda(false);
    }
  }, [sucursalVenta]);

  useEffect(() => {
    if (!sucursalVenta) {
      catalogIndexesRef.current = null;
      setCatalogReady(false);
      setCatalogCount(0);
      setCatalogUsingSaved(false);
      return undefined;
    }

    if (orgId) {
      const cached = loadCatalogFromStorage(getCatalogStorageKey(orgId, sucursalVenta));
      if (cached?.products?.length) {
        applyCatalog(cached.products, { fromCache: true });
        catalogLastRefreshRef.current = cached.fetchedAt || 0;
      }
    }

    refreshCatalogoCajero({ silent: true });

    const intervalId = window.setInterval(() => {
      if (Date.now() - catalogLastRefreshRef.current >= CATALOG_REFRESH_MS) {
        refreshCatalogoCajero({ silent: true });
      }
    }, CATALOG_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [sucursalVenta, orgId, applyCatalog, refreshCatalogoCajero]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !sucursalVenta) return;
      if (Date.now() - catalogLastRefreshRef.current >= CATALOG_REFRESH_MS) {
        refreshCatalogoCajero({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [sucursalVenta, refreshCatalogoCajero]);

  useEffect(() => {
    if (!sucursalVenta || busqueda.trim().length < NAME_SEARCH_MIN_CHARS) {
      setResultados([]);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const texto = busqueda.trim();
      const indexes = catalogIndexesRef.current;

      if (indexes?.count > 0) {
        const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
        const local = searchProductsByName(indexes, texto, MAX_NAME_SEARCH_RESULTS);
        perfLog('nombre-local', (typeof performance !== 'undefined' ? performance.now() : 0) - t0, {
          hits: local.length,
          catalog: indexes.count
        });
        setResultados(local);

        if (local.length === 0 && isOnline) {
          buscarProductosApi(texto);
        }
        return;
      }

      if (isOnline) {
        buscarProductosApi(texto);
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [busqueda, sucursalVenta, catalogReady, isOnline, buscarProductosApi]);

  useEffect(() => {
    if (!mostrarClientes) return undefined;

    const timer = setTimeout(async () => {
      try {
        setLoadingClientes(true);
        const termino = busquedaCliente.trim();
        const clientes = termino
          ? await clientesService.buscar(termino)
          : await clientesService.obtenerActivos();
        setClientesEncontrados((Array.isArray(clientes) ? clientes : []).slice(0, 20));
      } catch (error) {
        console.error('[MOBILE POS] Error buscando clientes:', error);
        toast.error('No se pudieron buscar clientes');
      } finally {
        setLoadingClientes(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [busquedaCliente, mostrarClientes]);

  const total = useMemo(() => (
    carrito.reduce((sum, item) => sum + lineaCarritoTotal(item), 0)
  ), [carrito]);

  const saldoOficialDeuda = Math.max(0, Number(saldoOficialCliente?.saldo_actual) || 0);
  const deudaTotalCliente = saldoOficialDeuda;

  const metodoPagoActivo = useMemo(() => normalizarMetodoPagoCajero(metodoPago), [metodoPago]);
  const cobroEnEfectivo = esMetodoEfectivoCajero(metodoPagoActivo);
  const cobroEnCredito = isMetodoCredito(metodoPagoActivo);
  const clienteCajeroConId = Boolean(clienteSeleccionado?.id);

  const limiteCreditoCajero = useMemo(() => {
    if (!clienteCajeroConId) return null;
    const limite = parseLimiteCreditoCliente(clienteSeleccionado);
    if (limite <= 0) return null;
    const deudaActual = Math.max(0, Number(saldoOficialCliente?.saldo_actual) || 0);
    return {
      limite,
      disponible: calcularDisponibleCredito(limite, deudaActual),
      deudaActual
    };
  }, [clienteCajeroConId, clienteSeleccionado, saldoOficialCliente]);

  const mostrarMetodoCuentaCorriente = cajaClientesHabilitado && clienteCajeroConId;
  const montoRecibidoNum = parseFloat(montoRecibido || 0) || 0;

  const resumenPagoCajero = useMemo(() => {
    const montoParaCalculo = cobroEnEfectivo || cobroEnCredito
      ? montoRecibidoNum
      : total;
    return computeVentaPago({
      metodoPago: metodoPagoActivo,
      total,
      montoPagado: montoParaCalculo,
      estadoPagoUi: cobroEnCredito ? 'pendiente' : 'completada'
    });
  }, [metodoPagoActivo, total, montoRecibidoNum, cobroEnEfectivo, cobroEnCredito]);

  const seleccionarMetodoPago = useCallback((metodo) => {
    const normalizado = normalizarMetodoPagoCajero(metodo);
    if (!esMetodoPagoCajeroPermitido(normalizado, clienteSeleccionado?.id)) {
      return;
    }
    setMetodoPago(normalizado);
    if (isMetodoCredito(normalizado)) {
      setMontoRecibido('');
      return;
    }
    if (!esMetodoEfectivoCajero(normalizado)) {
      setMontoRecibido((prev) => {
        const prevNum = parseFloat(prev || 0) || 0;
        if (total > 0) return String(total);
        return prevNum > 0 ? prev : '';
      });
    }
  }, [total, clienteSeleccionado?.id]);

  useEffect(() => {
    if (cobroEnEfectivo || cobroEnCredito || total <= 0) return;
    setMontoRecibido((prev) => {
      const prevNum = parseFloat(prev || 0) || 0;
      if (prevNum >= total) return prev;
      return String(total);
    });
  }, [cobroEnEfectivo, cobroEnCredito, total, metodoPagoActivo]);

  useEffect(() => {
    if (clienteCajeroConId || !cobroEnCredito) return;
    setMetodoPago(METODO_PAGO_CAJERO_DEFAULT);
    setMontoRecibido('');
  }, [clienteCajeroConId, cobroEnCredito]);

  const vuelto = cobroEnEfectivo
    ? Math.max(0, montoRecibidoNum - total)
    : 0;

  const flujoPostVentaActivo = Boolean(ticketPendiente) || Boolean(ticketVentaPayload?.venta);

  const cerrarPostVentaYContinuar = useCallback(() => {
    setTicketPendiente(null);
    setTicketVentaPayload(null);
    setBusqueda('');
    enfocarBusquedaPOS();
  }, [enfocarBusquedaPOS]);

  const normalizeVentaForTicket = useCallback((ticket) => {
    if (!ticket) return null;
    const vendedorLabel = getUserFullName(currentUser);
    const detalles = (ticket.detalles || []).map((d) => ({
      ...d,
      producto_info: d.producto_info || (d.nombre ? { nombre: d.nombre, codigo: d.codigo } : { nombre: 'Producto' }),
      nombre_producto: d.nombre_producto || d.nombre,
      precio_unitario: d.precio_unitario != null ? d.precio_unitario : d.precio,
      precio_total: d.precio_total != null ? d.precio_total : (d.precio || d.precio_unitario || 0) * (d.cantidad || 0)
    }));
    return {
      ...ticket,
      detalles,
      usuario_nombre: ticket.usuario_nombre || vendedorLabel,
      vendedor: ticket.vendedor || vendedorLabel,
      fecha: ticket.fecha || new Date().toISOString(),
      metodo_pago: normalizarMetodoPagoCajero(ticket.metodo_pago || metodoPago || 'efectivo'),
      estado: ticket.estado || 'completada',
      subtotal: ticket.subtotal != null ? ticket.subtotal : ticket.total,
      descuento: ticket.descuento != null ? ticket.descuento : 0
    };
  }, [currentUser, metodoPago]);

  const abrirTicketVenta = useCallback((ticket, autoPrint) => {
    const normalized = normalizeVentaForTicket(ticket);
    if (!normalized) return;
    setTicketVentaPayload({ venta: normalized, autoPrint: !!autoPrint });
  }, [normalizeVentaForTicket]);

  const seleccionarConsumidorFinal = () => {
    if (cobroEnCredito) {
      setMetodoPago(METODO_PAGO_CAJERO_DEFAULT);
      setMontoRecibido('');
    }
    setClienteSeleccionado(CONSUMIDOR_FINAL);
    setFilasComprobantesPagables([]);
    setSaldoOficialCliente(null);
    setMostrarDeudaCliente(false);
    setModalAlertaDeudaInicial(false);
    setPagoDeudaDialog({ open: false, venta: null });
    deudaAlertaContinuadaRef.current = null;
    setMostrarClientes(false);
    setBusquedaCliente('');
    setClientesEncontrados([]);
  };

  const actualizarSaldosClienteCajero = useCallback(async (cliente) => {
    if (!cliente?.id) return null;

    const saldoInfo = await clientesService.calcularSaldoCliente(cliente.id);
    setSaldoOficialCliente(saldoInfo);

    let filas = [];
    const necesitaComprobantes = cajaAlertaDeudasHabilitada || cajaPagoDeudasHabilitado;
    if (necesitaComprobantes) {
      try {
        const [cuentasRaw, config] = await Promise.all([
          ventasService.obtenerCuentasPorCobrar({ cliente_id: cliente.id }),
          configuracionService.obtener()
        ]);
        const diasConfig = normalizarDeudaVencidaDias(config?.pos_politicas?.deuda_vencida_dias);
        let ventas = extractVentasCliente(cuentasRaw, cliente.id);
        const saldoBackend = parseFloat(saldoInfo?.saldo_actual) || 0;
        if (ventas.length === 0 && saldoBackend > 0) {
          const cuentasGlobal = await ventasService.obtenerCuentasPorCobrar();
          ventas = extractVentasCliente(cuentasGlobal, cliente.id);
        }
        filas = buildFilasCuentaCorriente(ventas, diasConfig);
      } catch (error) {
        console.warn('[MOBILE POS] No se pudieron cargar comprobantes pagables:', error.message);
        filas = [];
      }
    }
    setFilasComprobantesPagables(filas);

    return { saldoInfo, filas };
  }, [cajaAlertaDeudasHabilitada, cajaPagoDeudasHabilitado]);

  const seleccionarCliente = async (cliente) => {
    setClienteSeleccionado(cliente);
    setMostrarClientes(false);
    setBusquedaCliente('');
    setClientesEncontrados([]);
    setMostrarDeudaCliente(false);
    setModalAlertaDeudaInicial(false);
    setPagoDeudaDialog({ open: false, venta: null });

    if (!cliente?.id) {
      setFilasComprobantesPagables([]);
      setSaldoOficialCliente(null);
      deudaAlertaContinuadaRef.current = null;
      return;
    }

    if (deudaAlertaContinuadaRef.current !== cliente.id) {
      deudaAlertaContinuadaRef.current = null;
    }

    try {
      const { saldoInfo } = await actualizarSaldosClienteCajero(cliente) || {};

      const tieneDeudaOficial = clienteTieneDeudaOficial(saldoInfo);
      const yaContinuoConEsteCliente = deudaAlertaContinuadaRef.current === cliente.id;

      if (tieneDeudaOficial && !yaContinuoConEsteCliente && cajaClientesHabilitado) {
        setModalAlertaDeudaInicial(true);
      } else if (!tieneDeudaOficial) {
        setFilasComprobantesPagables([]);
      }
    } catch (error) {
      console.warn('[MOBILE POS] No se pudo consultar datos del cliente:', error.message);
      setFilasComprobantesPagables([]);
      setSaldoOficialCliente(null);
      setMostrarDeudaCliente(false);
      setModalAlertaDeudaInicial(false);
    }
  };

  const continuarVentaConDeuda = () => {
    if (clienteSeleccionado?.id) {
      deudaAlertaContinuadaRef.current = clienteSeleccionado.id;
    }
    setModalAlertaDeudaInicial(false);
  };

  const iniciarCobroDeudaCajero = () => {
    setModalAlertaDeudaInicial(false);
    if (!cajaPagoDeudasHabilitado) {
      toast.warning('El cobro de deudas no está habilitado en esta caja');
      return;
    }

    const pagables = filasComprobantesPagables;
    if (pagables.length === 1) {
      setPagoDeudaDialog({
        open: true,
        venta: filaComprobanteAVentaCajero(pagables[0], clienteSeleccionado)
      });
      return;
    }
    if (pagables.length > 1) {
      setMostrarDeudaCliente(true);
      return;
    }
    toast.warning(MENSAJE_SALDO_SIN_COMPROBANTES_CAJA);
  };

  const registrarPagoDeudaCajero = async (pagoData) => {
    const ventaId = pagoDeudaDialog.venta?.id;
    if (!ventaId) {
      throw new Error('Comprobante no válido para cobrar');
    }
    await ventasService.registrarPago(ventaId, {
      ...pagoData,
      concepto: pagoData.concepto || `Pago desde cajero — ${pagoDeudaDialog.venta.numero}`
    });
    toast.success('Pago registrado correctamente');
    if (clienteSeleccionado?.id) {
      const result = await actualizarSaldosClienteCajero(clienteSeleccionado);
      if (!clienteTieneDeudaOficial(result?.saldoInfo)) {
        setMostrarDeudaCliente(false);
      }
    }
  };

  const pagarComprobanteDeuda = async () => {
    if (!comprobanteDeuda?.id) {
      toast.error('Este saldo no tiene un comprobante pagable asociado');
      return;
    }

    const monto = parseFloat(montoPagoDeuda || 0);
    if (!monto || monto <= 0) {
      toast.error('Ingresá un monto válido');
      return;
    }

    try {
      setPagandoDeuda(true);
      await ventasService.registrarPago(comprobanteDeuda.id, {
        monto,
        medio_pago: medioPagoDeuda,
        concepto: `Pago desde caja del comprobante ${comprobanteDeuda.numero || comprobanteDeuda.id}`
      });
      toast.success('Pago registrado correctamente');
      setComprobanteDeuda(null);
      setMontoPagoDeuda('');
      if (clienteSeleccionado?.id) {
        const result = await actualizarSaldosClienteCajero(clienteSeleccionado);
        if (!clienteTieneDeudaOficial(result?.saldoInfo)) {
          setMostrarDeudaCliente(false);
        }
      }
    } catch (error) {
      console.error('[MOBILE POS] Error pagando deuda:', error);
      toast.error(error?.message || 'No se pudo registrar el pago');
    } finally {
      setPagandoDeuda(false);
    }
  };

  const abrirProductoNoEncontrado = (codigo) => {
    searchInputRef.current?.blur();
    setProductoNoEncontrado({ open: true, codigo: String(codigo || '').trim() });
  };

  const notificarProductoBalanzaNoEncontrado = (plu) => {
    toast.error(`Producto de balanza no encontrado (PLU ${plu})`);
    setBusqueda('');
    enfocarBusquedaPOS();
  };

  const resolverProductoEncontrado = (producto, { scaleData } = {}) => {
    if (!producto?.id) {
      if (scaleData?.plu) {
        notificarProductoBalanzaNoEncontrado(scaleData.plu);
        return;
      }
      abrirProductoNoEncontrado(scaleData?.productCode || producto?.codigo);
      return;
    }
    if (bloquearOActivarProducto(producto)) return;
    if (scaleData) {
      if (scaleData.weightKg <= 0) {
        toast.warning('Etiqueta de balanza inválida: peso no reconocido');
        return;
      }
      agregarProducto(producto, {
        forceNewLine: true,
        cantidad: scaleData.weightKg,
        codigoBalanza: scaleData.barcode
      });
      toast.success(`Etiqueta balanza: ${scaleData.weightKg.toFixed(3)} kg de ${producto.nombre}`);
      return;
    }
    if (isProductoPesoManual(producto) && posPoliticas.permitir_venta_peso_manual) {
      searchInputRef.current?.blur();
      const normalized = normalizeForCajero(producto);
      const precioKg = obtenerPrecioVentaCajero(normalized);
      setPesoManualVenta({
        open: true,
        producto: {
          ...normalized,
          precioPorKg: precioKg,
          precio_por_kg: precioKg,
          precio_venta: precioKg
        }
      });
      return;
    }
    agregarProducto(producto);
  };

  const ejecutarComandoPosRef = useRef(null);

  const buscarPorCodigoExacto = async () => {
    if (ticketPendiente || ticketVentaPayload) {
      toast.info('Tocá «Siguiente venta» para continuar escaneando');
      return;
    }
    const codigo = busqueda.trim();
    if (!codigo || !sucursalVenta) return;

    const cmdId = parsePosScanCommand(codigo);
    if (cmdId) {
      ejecutarComandoPosRef.current?.(cmdId);
      setBusqueda('');
      enfocarBusquedaPOS();
      return;
    }

    const scaleData = parseScaleBarcode(codigo);
    const lookupKeys = scaleData
      ? [scaleData.plu]
      : resolveCatalogLookupKeys(codigo);
    const codigoBusqueda = lookupKeys[0] || codigo.trim();

    const tLocal0 = typeof performance !== 'undefined' ? performance.now() : 0;
    let localProducto = scaleData
      ? lookupProductByScalePlu(catalogIndexesRef.current, scaleData.plu)
      : lookupProductByCode(catalogIndexesRef.current, codigo);
    const cacheStale = catalogUsingSaved
      || isCatalogStale(catalogLastRefreshRef.current, CATALOG_PRICE_STALE_MS);

    const stockLocal = localProducto ? obtenerStockSucursal(localProducto) : 0;
    const onlineNow = isOnline && (typeof navigator === 'undefined' || navigator.onLine);
    const needsFreshStock = localProducto?.id && onlineNow && (cacheStale || stockLocal <= 0);
    if (needsFreshStock) {
      try {
        const fresh = scaleData
          ? await productosService.obtenerPorPluBalanzaConStock(scaleData.plu, sucursalVenta)
          : await productosService.obtenerPorCodigoConStock(codigoBusqueda, sucursalVenta);
        if (fresh?.id) {
          upsertForCajero(catalogIndexesRef.current, fresh);
          localProducto = normalizeForCajero(fresh);
          perfLog('codigo-api-precio', (typeof performance !== 'undefined' ? performance.now() : 0) - tLocal0, {
            codigo: codigoBusqueda,
            stale: cacheStale,
            stockRefresh: stockLocal <= 0
          });
        }
      } catch (error) {
        console.warn('[MOBILE POS] No se pudo refrescar stock/precio por API:', error?.message || error);
      }
    }

    if (localProducto?.id) {
      perfLog('codigo-local', (typeof performance !== 'undefined' ? performance.now() : 0) - tLocal0, {
        codigo: codigoBusqueda,
        catalog: catalogIndexesRef.current?.count || 0
      });
      resolverProductoEncontrado(localProducto, { scaleData });
      return;
    }

    if (!onlineNow) {
      if (scaleData?.plu) {
        notificarProductoBalanzaNoEncontrado(scaleData.plu);
        return;
      }
      toast.error(
        `Sin conexión: «${codigo}» no está en el catálogo local. Conectá internet y actualizá el catálogo.`,
        { autoClose: 6000 }
      );
      abrirProductoNoEncontrado(codigo);
      return;
    }

    try {
      setLoadingBusqueda(true);
      const tApi0 = typeof performance !== 'undefined' ? performance.now() : 0;
      const producto = scaleData
        ? await productosService.obtenerPorPluBalanzaConStock(scaleData.plu, sucursalVenta)
        : await productosService.obtenerPorCodigoConStock(codigoBusqueda, sucursalVenta);
      perfLog('codigo-api', (typeof performance !== 'undefined' ? performance.now() : 0) - tApi0, {
        codigo: codigoBusqueda,
        scalePlu: scaleData?.plu || null
      });

      if (!producto || !producto.id) {
        if (scaleData?.plu) {
          notificarProductoBalanzaNoEncontrado(scaleData.plu);
          return;
        }
        abrirProductoNoEncontrado(codigoBusqueda);
        return;
      }
      upsertForCajero(catalogIndexesRef.current, producto);
      resolverProductoEncontrado(normalizeForCajero(producto), { scaleData });
    } catch (error) {
      console.error('[MOBILE POS] Error al buscar por código:', error);
      abrirProductoNoEncontrado(codigoBusqueda);
    } finally {
      setLoadingBusqueda(false);
    }
  };

  const agregarProducto = (producto, options = {}) => {
    if (ticketPendiente || ticketVentaPayload) {
      toast.info('Tocá «Siguiente venta» para continuar escaneando');
      return;
    }
    if (bloquearOActivarProducto(producto)) {
      return;
    }

    const forceNewLine = options.forceNewLine === true;
    const cantidadInicial = Number.isFinite(options.cantidad) ? options.cantidad : 1;
    const stockDisponible = obtenerStockSucursal(producto);
    const normalized = normalizeForCajero(producto);
    const precio = obtenerPrecioVentaCajero(normalized);

    if (!hasValidCajeroPrice(normalized)) {
      if (isProductoPendienteDePrecio(normalized)) {
        notifyProductoPendientePrecio(normalized);
      } else {
        toast.error(`Producto sin precio configurado: ${normalized.nombre || 'Sin nombre'}`);
      }
      return;
    }

    if (stockDisponible <= 0 && !posPoliticas.permitir_venta_stock_negativo) {
      toast.error(`Stock insuficiente: "${producto.nombre}"`);
      return;
    }
    if (stockDisponible <= 0 && posPoliticas.permitir_venta_stock_negativo) {
      toast.warning(`Stock agotado: "${producto.nombre}". La venta quedará en negativo.`);
    }

    setCarrito((items) => {
      const existente = items.find((item) => item.producto_id === producto.id);
      if (existente && !forceNewLine) {
        if (
          existente.stock_disponible > 0 &&
          existente.cantidad >= existente.stock_disponible &&
          !posPoliticas.permitir_venta_stock_negativo
        ) {
          toast.error(`Stock insuficiente: "${existente.nombre}"`);
          return items;
        }
        if (
          existente.stock_disponible > 0 &&
          existente.cantidad >= existente.stock_disponible &&
          posPoliticas.permitir_venta_stock_negativo
        ) {
          toast.warning(`Stock agotado: "${existente.nombre}". La venta quedará en negativo.`);
        }

        return items.map((item) => (
          item.id === existente.id
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        ));
      }

      return [
        ...items,
        {
          id: forceNewLine ? `${producto.id}__${Date.now()}` : producto.id,
          producto_id: producto.id,
          codigo: producto.codigo || producto.codigo_barras || '',
          nombre: producto.nombre || 'Producto sin nombre',
          precio,
          cantidad: cantidadInicial,
          stock_disponible: stockDisponible,
          codigo_balanza: options.codigoBalanza || null
        }
      ];
    });

    setBusqueda('');
    setResultados([]);
    setUltimaVenta(null);
    enfocarBusquedaPOS();
  };

  const agregarProductoPesoManual = (linea) => {
    const producto = linea.producto;
    if (!producto?.id) return;
    const stockDisponible = obtenerStockSucursal(producto);
    const codigo = producto.codigo || producto.codigoManual || '';
    setCarrito((items) => [
      ...items,
      {
        id: `${producto.id}__peso__${Date.now()}`,
        producto_id: producto.id,
        codigo,
        codigo_interno: isCodigoInterno(codigo),
        nombre: producto.nombre || 'Producto',
        precio: linea.precioPorKg,
        cantidad: linea.pesoKg,
        stock_disponible: stockDisponible,
        tipoVenta: TIPO_VENTA_PESO_MANUAL,
        pesoKg: linea.pesoKg,
        precioPorKg: linea.precioPorKg,
        subtotal: linea.subtotalFinal,
        subtotalCalculado: linea.subtotalCalculado,
        subtotalFinal: linea.subtotalFinal,
        redondeoAplicado: linea.redondeoAplicado,
        precio_linea: linea.subtotalFinal,
        categoriaPeso: producto.categoriaPeso || null
      }
    ]);
    setBusqueda('');
    setResultados([]);
    setUltimaVenta(null);
    enfocarBusquedaPOS();
    toast.success(`${producto.nombre} agregado`);
  };

  abrirPesoManualRef.current = (producto) => {
    if (!posPoliticas.permitir_venta_peso_manual) {
      toast.error('Venta por peso manual deshabilitada en configuración');
      return;
    }
    const normalized = normalizeForCajero(producto);
    const precioKg = obtenerPrecioVentaCajero(normalized);
    setPesoManualVenta({
      open: true,
      producto: {
        ...normalized,
        precioPorKg: precioKg,
        precio_por_kg: precioKg,
        precio_venta: precioKg
      }
    });
  };

  agregarProductoRef.current = agregarProducto;

  const handleProductoRapidoCreado = (creado, { vender } = {}) => {
    const normalized = normalizeForCajero(creado);
    upsertForCajero(catalogIndexesRef.current, normalized);
    setProductoNoEncontrado({ open: false, codigo: '' });
    if (vender) {
      if (isProductoPesoManual(normalized)) {
        abrirPesoManualRef.current(normalized);
      } else if (agregarProductoRef.current) {
        agregarProductoRef.current(normalized);
      }
    } else {
      enfocarBusquedaPOS();
    }
  };

  const handleActivacionMasterSuccess = (updated, estado, { accion } = {}) => {
    setActivacionMaster({ open: false, producto: null });
    const normalized = normalizeForCajero(updated);
    upsertForCajero(catalogIndexesRef.current, normalized);
    const estadoFinal = estado || getEstadoProductoMaster(normalized, normalized.stock_sucursal);
    if (accion === 'vender' && estadoFinal === 'listo' && agregarProductoRef.current) {
      agregarProductoRef.current(normalized);
    }
    enfocarBusquedaPOS();
  };

  const actualizarCantidad = (id, cantidad) => {
    setCarrito((items) => items.flatMap((item) => {
      if (item.id !== id) return [item];
      if (cantidad <= 0) return [];

      return [{
        ...item,
        cantidad
      }];
    }));
  };

  const limpiarVenta = () => {
    setCarrito([]);
    setMontoRecibido('');
    setMetodoPago('efectivo');
    setResultados([]);
    setBusqueda('');
    setMostrarCarritoCompleto(false);
    setClienteSeleccionado(CONSUMIDOR_FINAL);
    setFilasComprobantesPagables([]);
    setSaldoOficialCliente(null);
    setMostrarDeudaCliente(false);
    setModalAlertaDeudaInicial(false);
    setPagoDeudaDialog({ open: false, venta: null });
    deudaAlertaContinuadaRef.current = null;
    setComprobanteDeuda(null);
    clearPosSession();
    enfocarBusquedaPOS();
  };

  const revalidarPreciosCarrito = useCallback(async (items) => {
    if (!isOnline || !items.length || !sucursalVenta) return items;

    const uniqueIds = [...new Set(items.map((item) => item.producto_id).filter(Boolean))];
    if (!uniqueIds.length) return items;

    const priceById = new Map();
    await Promise.all(uniqueIds.map(async (productoId) => {
      try {
        const producto = await productosService.obtenerPorId(productoId);
        if (producto?.id) {
          priceById.set(
            String(producto.id),
            obtenerPrecioVentaCajero(normalizeForCajero(producto))
          );
        }
      } catch {
        // Mantener precio del carrito si falla la consulta puntual.
      }
    }));

    if (!priceById.size) return items;

    return items.map((item) => {
      const freshPrice = priceById.get(String(item.producto_id));
      if (freshPrice == null || !Number.isFinite(freshPrice) || freshPrice <= 0) {
        return item;
      }
      if (freshPrice === item.precio) return item;
      return { ...item, precio: freshPrice };
    });
  }, [isOnline, sucursalVenta]);

  const finalizarVenta = async (stockNegOverride = null) => {
    if (ventaEnProcesoRef.current || procesandoVenta) {
      toast.info('Venta en proceso');
      return;
    }
    if (ticketPendiente || ticketVentaPayload) {
      toast.info('Venta en proceso');
      return;
    }

    if (!sucursalVenta) {
      toast.error('Seleccioná una sucursal para vender');
      return;
    }

    if (carrito.length === 0) {
      toast.error('Agregá al menos un producto');
      return;
    }

    const transactionId = crearTransactionIdVenta();
    if (ventaTransactionIdRef.current === transactionId) {
      toast.info('Venta en proceso');
      return;
    }

    ventaEnProcesoRef.current = true;
    setCajeroVentaEnProceso(true);
    ventaTransactionIdRef.current = transactionId;
    setProcesandoVenta(true);

    let carritoFinal = carrito;
    let venta = null;
    let detalles = null;
    let totalVenta = 0;
    let vueltoVenta = 0;
    let metodoPagoVenta = metodoPagoActivo;

    try {
      try {
        carritoFinal = await revalidarPreciosCarrito(carrito);
        if (carritoFinal !== carrito) {
          setCarrito(carritoFinal);
        }
      } catch (error) {
        console.warn('[MOBILE POS] No se pudieron revalidar precios antes de cobrar:', error?.message || error);
      }

      try {
        const stockRefresh = await productosService.revalidarStockCarrito(carritoFinal, sucursalVenta);
        if (!stockRefresh.ok) {
          toast.error(`Producto ${stockRefresh.item?.nombre || 'desconocido'} no encontrado`);
          return;
        }
        carritoFinal = stockRefresh.carrito;
        setCarrito(carritoFinal);
      } catch (error) {
        console.warn('[MOBILE POS] No se pudo revalidar stock antes de cobrar:', error?.message || error);
      }

      totalVenta = carritoFinal.reduce((sum, item) => sum + lineaCarritoTotal(item), 0);

      const lineasSinPrecio = carritoFinal.filter((item) => !item.precio || item.precio <= 0);
      if (lineasSinPrecio.length > 0) {
        toast.error(`Producto sin precio configurado: ${lineasSinPrecio.map((i) => i.nombre).join(', ')}`);
        return;
      }

      if (cobroEnEfectivo && montoRecibidoNum < totalVenta) {
        toast.warning('El efectivo recibido no alcanza para cubrir el total actualizado');
        return;
      }

      const metodoPagoVentaLocal = metodoPagoActivo;
      if (isMetodoCredito(metodoPagoVentaLocal) && !clienteSeleccionado?.id) {
        toast.error('Debe seleccionar un cliente para cuenta corriente');
        return;
      }
      if (!esMetodoPagoCajeroPermitido(metodoPagoVentaLocal, clienteSeleccionado?.id)) {
        toast.error('Método de pago inválido para esta venta');
        return;
      }
      metodoPagoVenta = metodoPagoVentaLocal;

      const pagoCalculado = computeVentaPago({
        metodoPago: metodoPagoVentaLocal,
        total: totalVenta,
        montoPagado: cobroEnEfectivo || isMetodoCredito(metodoPagoVentaLocal)
          ? montoRecibidoNum
          : totalVenta,
        estadoPagoUi: isMetodoCredito(metodoPagoVentaLocal) ? 'pendiente' : 'completada'
      });

      if (
        isMetodoCredito(metodoPagoVentaLocal) &&
        !creditoExcedidoAceptadoRef.current
      ) {
        const evalLimite = evaluarLimiteCreditoVenta({
          cliente: clienteSeleccionado,
          saldoActual: saldoOficialCliente?.saldo_actual,
          saldoPendienteVenta: pagoCalculado.saldo_pendiente
        });
        if (evalLimite.excedeLimite) {
          pendingCheckoutRef.current = {
            carritoFinal,
            totalVenta,
            montoRecibidoNum,
            metodoPagoVenta,
            transactionId,
            stockNegOverride: stockNegOverride || null
          };
          setModalCreditoExcedido({
            ...evalLimite,
            clienteNombre: nombreCliente(clienteSeleccionado)
          });
          return;
        }
      }

      const lineasStockInsuficiente = carritoFinal.filter(
        (item) => Number(item.cantidad) > Number(item.stock_disponible)
      );
      let stockNegExtras = {};
      if (lineasStockInsuficiente.length > 0) {
        if (!posPoliticas.permitir_venta_stock_negativo) {
          toast.error(`Stock insuficiente: ${lineasStockInsuficiente.map((i) => i.nombre).join(', ')}`);
          return;
        }
        stockNegExtras = { permite_stock_negativo: true };
        if (posPoliticas.requiere_autorizacion_stock_negativo && !stockNegOverride) {
          pendingCheckoutRef.current = {
            carritoFinal,
            totalVenta,
            montoRecibidoNum,
            metodoPagoVenta,
            transactionId
          };
          setLineasStockNegativo(lineasStockInsuficiente);
          setStockNegConfirmChecked(false);
          setStockNegAdminEmail('');
          setStockNegAdminPassword('');
          setStockNegMotivo('');
          setModalStockNegativo(true);
          return;
        }
        if (stockNegOverride) {
          stockNegExtras = { ...stockNegExtras, ...stockNegOverride };
        }
      }

      const montoPagadoVenta = pagoCalculado.total_pagado;
      const saldoPendienteVenta = pagoCalculado.saldo_pendiente;
      const estadoVenta = pagoCalculado.estado;
      const estadoPagoVenta = pagoCalculado.estado_pago;
      const montoRecibidoVenta = cobroEnEfectivo || isMetodoCredito(metodoPagoVentaLocal)
        ? montoRecibidoNum
        : totalVenta;
      vueltoVenta = cobroEnEfectivo
        ? Math.max(0, montoRecibidoNum - totalVenta)
        : 0;
      const pagosVenta = isMetodoCredito(metodoPagoVentaLocal)
        ? (montoPagadoVenta > 0
          ? [{
            monto: montoPagadoVenta,
            metodo_pago: metodoPagoVentaLocal,
            medio_pago: metodoPagoVentaLocal,
            concepto: 'Anticipo cuenta corriente'
          }]
          : [])
        : [{
          monto: montoPagadoVenta,
          metodo_pago: metodoPagoVentaLocal,
          medio_pago: metodoPagoVentaLocal,
          concepto: 'Pago en mostrador'
        }];
      venta = {
        sucursal_id: sucursalVenta,
        cliente_id: clienteSeleccionado?.id || null,
        cliente_nombre: clienteSeleccionado?.id ? nombreCliente(clienteSeleccionado) : 'Cliente General',
        cliente_info: clienteSeleccionado?.id
          ? {
              id: clienteSeleccionado.id,
              nombre: clienteSeleccionado.nombre || '',
              apellido: clienteSeleccionado.apellido || '',
              nombre_completo: nombreCliente(clienteSeleccionado)
            }
          : {
              nombre: '',
              apellido: '',
              nombre_completo: 'Cliente General'
            },
        usuario_id: currentUser?.id || null,
        metodo_pago: metodoPagoVenta,
        subtotal: totalVenta,
        descuento: 0,
        total: totalVenta,
        estado: estadoVenta,
        lista_precio_aplicada: listaPrecioCajero,
        monto_pagado: montoPagadoVenta,
        total_pagado: montoPagadoVenta,
        monto_recibido: montoRecibidoVenta,
        vuelto: vueltoVenta,
        saldo_pendiente: saldoPendienteVenta,
        estado_pago: estadoPagoVenta,
        fecha_dia_negocio: fechaDiaNegocioLocal(),
        client_transaction_id: transactionId,
        pagos: pagosVenta,
        ...stockNegExtras
      };

      detalles = carritoFinal.map((item) => {
        const esPeso = item.tipoVenta === TIPO_VENTA_PESO_MANUAL;
        const totalLinea = lineaCarritoTotal(item);
        return {
          producto_id: item.producto_id || item.id,
          cantidad: esPeso ? item.pesoKg : item.cantidad,
          cantidadParaStock: esPeso ? item.pesoKg : item.cantidad,
          precio_unitario: esPeso ? item.precioPorKg : item.precio,
          precio_lista: esPeso ? item.precioPorKg : item.precio,
          lista_aplicada: listaPrecioCajero,
          precio_total: totalLinea,
          codigo_balanza: item.codigo_balanza || null,
          tipoVenta: item.tipoVenta || 'unidad',
          pesoKg: item.pesoKg ?? null,
          precioPorKg: item.precioPorKg ?? null,
          subtotalCalculado: item.subtotalCalculado ?? null,
          subtotalFinal: item.subtotalFinal ?? null,
          redondeoAplicado: item.redondeoAplicado ?? null,
          producto_info: {
            id: item.producto_id || item.id,
            codigo: item.codigo,
            nombre: item.nombre,
            precio: esPeso ? item.precioPorKg : item.precio,
            tipoVenta: item.tipoVenta || 'unidad',
            pesoKg: item.pesoKg,
            precioPorKg: item.precioPorKg,
            subtotalFinal: item.subtotalFinal
          }
        };
      });

      const respuesta = await ventasService.crear(venta, detalles, sucursalVenta);
      const ventaCreada = respuesta?.data?.data || respuesta?.data || respuesta || {};
      const ticket = {
        ...venta,
        id: ventaCreada.id,
        numero: ventaCreada.numero || ventaCreada.id,
        fecha: new Date().toISOString(),
        detalles: carritoFinal.map((item) => ({
          nombre: item.nombre,
          codigo: item.codigo,
          cantidad: item.tipoVenta === TIPO_VENTA_PESO_MANUAL ? item.pesoKg : item.cantidad,
          precio_unitario: item.tipoVenta === TIPO_VENTA_PESO_MANUAL ? item.precioPorKg : item.precio,
          precio_total: lineaCarritoTotal(item),
          tipoVenta: item.tipoVenta,
          pesoKg: item.pesoKg,
          precioPorKg: item.precioPorKg,
          subtotalCalculado: item.subtotalCalculado,
          subtotalFinal: item.subtotalFinal,
          redondeoAplicado: item.redondeoAplicado
        }))
      };
      setUltimaVenta({
        id: ticket.id,
        total: totalVenta,
        vuelto: vueltoVenta,
        metodoPago: metodoPagoVenta
      });
      toast.success(
        isMetodoCredito(metodoPagoVenta) && saldoPendienteVenta > 0
          ? `Venta en cuenta corriente — Saldo pendiente: ${formatMoneda(saldoPendienteVenta)}`
          : 'Venta registrada correctamente'
      );
      if (configTicket?.imprimir_ticket_automaticamente) {
        abrirTicketVenta(ticket, true);
      } else {
        setTicketPendiente(ticket);
      }
      limpiarVenta();
    } catch (error) {
      console.error('[MOBILE POS] Error al finalizar venta:', error);
      const msg = String(error?.message || '').toLowerCase();
      const networkError = !navigator.onLine || msg.includes('network') || msg.includes('fetch') || msg.includes('timeout') || msg.includes('conexion');
      if (networkError && venta && detalles) {
        const offlineTs = Date.now();
        const offlineTicket = {
          ...venta,
          id: `PEND-${offlineTs}`,
          numero: `PEND-${offlineTs}`,
          fecha: new Date().toISOString(),
          detalles: carritoFinal.map((item) => ({
            nombre: item.nombre,
            codigo: item.codigo,
            cantidad: item.cantidad,
            precio_unitario: item.precio,
            precio_total: item.precio * item.cantidad
          }))
        };
        try {
          enqueueOfflineSale({ venta, detalles, sucursalVenta });
        } catch (offlineErr) {
          toast.error(offlineErr?.message || 'No se pudo guardar la venta offline');
          return;
        }
        setUltimaVenta({ id: offlineTicket.id, total: totalVenta, vuelto: vueltoVenta, metodoPago: metodoPagoVenta });
        if (configTicket?.imprimir_ticket_automaticamente) abrirTicketVenta(offlineTicket, true);
        else setTicketPendiente(offlineTicket);
        limpiarVenta();
        toast.warning('Sin conexión: venta guardada como pendiente. Se sincronizará automáticamente al reconectar.');
      } else if (networkError) {
        toast.error('Sin conexión: no se pudo preparar la venta');
      } else {
        toast.error(error?.message || 'No se pudo registrar la venta');
      }
    } finally {
      ventaEnProcesoRef.current = false;
      setCajeroVentaEnProceso(false);
      ventaTransactionIdRef.current = null;
      setProcesandoVenta(false);
    }
  };

  const confirmarVentaCreditoExcedido = async () => {
    creditoExcedidoAceptadoRef.current = true;
    setModalCreditoExcedido(null);
    const pending = pendingCheckoutRef.current;
    pendingCheckoutRef.current = null;
    const override = pending?.stockNegOverride ?? null;
    await finalizarVenta(override || null);
    creditoExcedidoAceptadoRef.current = false;
  };

  const confirmarVentaStockNegativo = async () => {
    if (!stockNegConfirmChecked) {
      toast.warning('Marcá la casilla para autorizar venta con stock negativo');
      return;
    }
    if (!stockNegAdminEmail.trim() || !stockNegAdminPassword) {
      toast.warning('Ingresá credenciales de administrador');
      return;
    }
    const pending = pendingCheckoutRef.current;
    if (!pending) {
      setModalStockNegativo(false);
      return;
    }
    setModalStockNegativo(false);
    pendingCheckoutRef.current = null;
    await finalizarVenta({
      stock_negativo_autorizado: true,
      stock_negativo_admin_email: stockNegAdminEmail.trim(),
      stock_negativo_admin_password: stockNegAdminPassword,
      stock_negativo_motivo: stockNegMotivo.trim(),
      permite_stock_negativo: true
    });
  };

  const cobroBloqueado = procesandoVenta || Boolean(ticketPendiente) || Boolean(ticketVentaPayload) || modalStockNegativo || Boolean(modalCreditoExcedido);

  const hayModalCriticoAbierto = useCallback(
    () =>
      activacionMaster.open ||
      productoNoEncontrado.open ||
      pesoManualVenta.open ||
      mostrarClientes ||
      mostrarDeudaCliente ||
      modalAlertaDeudaInicial ||
      pagoDeudaDialog.open ||
      mostrarCarritoCompleto ||
      mostrarModalCierreCaja ||
      modalStockNegativo ||
      Boolean(modalCreditoExcedido) ||
      Boolean(ticketPendiente) ||
      Boolean(ticketVentaPayload),
    [
      activacionMaster.open,
      productoNoEncontrado.open,
      pesoManualVenta.open,
      mostrarClientes,
      mostrarDeudaCliente,
      modalAlertaDeudaInicial,
      pagoDeudaDialog.open,
      mostrarCarritoCompleto,
      mostrarModalCierreCaja,
      modalStockNegativo,
      modalCreditoExcedido,
      ticketPendiente,
      ticketVentaPayload
    ]
  );

  ejecutarComandoPosRef.current = (commandId) => {
    const label = POS_SCAN_COMMAND_LABELS[commandId] || commandId;
    const toastCmd = (msg) => toast.info(msg, { autoClose: 1500 });
    const cobroEnProceso =
      ventaEnProcesoRef.current ||
      procesandoVenta ||
      Boolean(ticketPendiente) ||
      Boolean(ticketVentaPayload) ||
      modalStockNegativo ||
      Boolean(modalCreditoExcedido);

    switch (commandId) {
      case 'FOCO':
      case 'BUSCAR':
        setBusqueda('');
        enfocarBusquedaPOS();
        toastCmd(`Comando: ${label}`);
        break;
      case 'LIMPIAR':
        setBusqueda('');
        enfocarBusquedaPOS();
        toastCmd(`Comando: ${label}`);
        break;
      case 'PANTALLA':
        if (fullscreenDisponible) {
          alternarPantallaCompleta();
          toastCmd(`Comando: ${label}`);
        }
        break;
      case 'EFECTIVO':
        seleccionarMetodoPago('efectivo');
        toastCmd(`Comando: ${label}`);
        break;
      case 'TARJETA':
        seleccionarMetodoPago('tarjeta');
        toastCmd(`Comando: ${label}`);
        break;
      case 'COBRAR':
        if (hayModalCriticoAbierto()) return;
        if (cobroEnProceso) return;
        if (carrito.length === 0) {
          toast.error('Agregá al menos un producto');
          return;
        }
        toastCmd(`Comando: ${label}`);
        finalizarVenta();
        break;
      case 'NUEVA':
        if (hayModalCriticoAbierto()) return;
        if (cobroEnProceso) return;
        toastCmd(`Comando: ${label}`);
        limpiarVenta();
        break;
      case 'CIERRE':
        if (hayModalCriticoAbierto()) return;
        toastCmd(`Comando: ${label}`);
        abrirModalCierreDesdeMostrador();
        break;
      default:
        break;
    }
  };

  /**
   * Venta Rápida — estado vivo para los hotkeys globales.
   * Se actualiza en cada render y lo lee el listener (registrado una sola vez)
   * para evitar closures obsoletos. No altera la lógica de cobro: solo invoca
   * las mismas funciones que los botones.
   */
  hotkeyStateRef.current = {
    enfocarBusquedaPOS,
    finalizarVenta,
    limpiarVenta,
    cobroBloqueado,
    ventaEnProcesoRef,
    searchInputEl: searchInputRef.current,
    hayModalAbierto:
      activacionMaster.open ||
      productoNoEncontrado.open ||
      pesoManualVenta.open ||
      mostrarClientes ||
      mostrarDeudaCliente ||
      modalAlertaDeudaInicial ||
      pagoDeudaDialog.open ||
      mostrarCarritoCompleto ||
      mostrarModalCierreCaja ||
      modalStockNegativo ||
      Boolean(modalCreditoExcedido) ||
      Boolean(ticketPendiente) ||
      Boolean(ticketVentaPayload)
  };

  useEffect(() => {
    const handler = (event) => {
      const s = hotkeyStateRef.current;
      if (!s) return;
      const key = event.key;
      const target = event.target;
      const tag = (target?.tagName || '').toLowerCase();
      const esCampoTexto =
        tag === 'input' || tag === 'textarea' || tag === 'select' || Boolean(target?.isContentEditable);
      const esScanner = target === s.searchInputEl;
      const escribiendoEnOtroCampo = esCampoTexto && !esScanner;
      const dineroRecibidoActivo = Boolean(target?.closest?.('[data-pos-busqueda-pausada]'));

      // Enter: comportamiento intacto (lo maneja el input de búsqueda).
      if (key === 'Enter') return;

      // F2: enfocar escáner. No actúa con un modal abierto.
      if (key === 'F2') {
        if (s.hayModalAbierto) return;
        event.preventDefault();
        s.enfocarBusquedaPOS();
        return;
      }

      // Alt+B: enfocar escáner; bloqueado mientras se escribe en otro campo.
      if (event.altKey && (key === 'b' || key === 'B')) {
        if (s.hayModalAbierto) return;
        if (escribiendoEnOtroCampo || dineroRecibidoActivo) return;
        event.preventDefault();
        s.enfocarBusquedaPOS();
        return;
      }

      if (s.hayModalAbierto) return;

      // Alt+C: cobrar — permitido con foco en "Dinero recibido" (flujo natural del cajero).
      if (event.altKey && (key === 'c' || key === 'C')) {
        event.preventDefault();
        if (!s.cobroBloqueado && !s.ventaEnProcesoRef.current) {
          s.finalizarVenta();
        }
        return;
      }

      // Alt+N y resto: no si se está tipeando en campos de texto o dinero recibido.
      if (escribiendoEnOtroCampo || dineroRecibidoActivo) return;

      // Alt+N: nueva venta, solo sin cobro en proceso.
      if (event.altKey && (key === 'n' || key === 'N')) {
        event.preventDefault();
        if (!s.cobroBloqueado && !s.ventaEnProcesoRef.current) {
          s.limpiarVenta();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const botonesMontoRapido = useMemo(() => {
    const redondeado = Math.ceil(total / 100) * 100;
    return [total, redondeado, redondeado + 500, redondeado + 1000]
      .filter((valor, index, arr) => valor > 0 && arr.indexOf(valor) === index);
  }, [total]);

  const busquedaLista = busqueda.trim().length >= NAME_SEARCH_MIN_CHARS;
  const mostrarResultados =
    loadingBusqueda || resultados.length > 0 || (busquedaLista && sucursalVenta);

  const gridResultadosClass = splitLandscapeLayout
    ? 'grid grid-cols-1 gap-1.5'
    : viewport.width >= 760
      ? 'grid grid-cols-2 gap-2 sm:gap-3'
      : 'grid grid-cols-1 gap-2 sm:gap-3';

  const resultadosSolo = (
    <>
      {mostrarResultados && (
        <div className={gridResultadosClass}>
          {loadingBusqueda && (
            <div className="rounded-xl border border-white/[0.06] bg-e0e3-surface/95 p-3 text-center text-sm font-semibold text-e0e3-muted shadow-card">
              Buscando productos...
            </div>
          )}

          {!loadingBusqueda && resultados.length === 0 && busquedaLista && (
            <div className="rounded-xl border border-white/[0.06] bg-e0e3-surface/95 p-3 text-center text-sm font-semibold text-e0e3-muted shadow-card">
              No se encontraron productos con stock para "{busqueda.trim()}".
            </div>
          )}

          {resultados.map((producto) => (
            <button
              key={producto.id}
              type="button"
              onClick={() => resolverProductoEncontrado(producto)}
              className={`rounded-xl border border-white/[0.06] bg-e0e3-surface/90 text-left shadow-card transition hover:border-e0e3-accent/40 active:scale-[0.99] ${
                landscapeTablet ? 'p-2.5' : 'p-4'
              }`}
            >
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                <div className="min-w-0">
                  <div className={`font-black text-white ${landscapeTablet ? 'text-sm leading-snug' : 'text-lg'}`}>{producto.nombre}</div>
                  <div className="text-xs text-e0e3-muted">Código: {producto.codigo || 'Sin código'}</div>
                  <div className={`mt-1 text-xs font-semibold ${obtenerStockSucursal(producto) > 0 ? 'text-e0e3-ok' : 'text-e0e3-danger'}`}>
                    Stock: {obtenerStockSucursal(producto)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`font-black text-e0e3-accentSoft ${landscapeTablet ? 'text-base' : 'text-xl'} tabular-nums`}>
                    {formatMoneda(obtenerPrecioVentaCajero(producto))}
                  </div>
                  <div className="mt-1.5 rounded-full border border-white/[0.08] bg-e0e3-accent/15 px-2 py-0.5 text-[11px] font-bold text-e0e3-accentSoft">
                    Agregar
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );

  const carritoMaxClass = landscapeTablet
    ? ''
    : `sm:max-h-[min(260px,40vh)] ${compact ? 'max-h-[30vh]' : 'max-h-[260px]'}`;

  const lineasCarrito = carrito.map((item) => (
    <div key={item.id} className="grid grid-cols-[1fr_auto] gap-2 border-b border-white/[0.06] bg-e0e3-surface/40 px-2 py-1.5 last:border-b-0 sm:px-3 sm:py-2">
      <button
        type="button"
        onClick={() => setMostrarCarritoCompleto(true)}
        className="min-w-0 text-left"
      >
        <div className="truncate text-sm font-black leading-tight text-white">
          {item.nombre}
        </div>
        <div className="text-xs font-semibold text-e0e3-muted">
          {item.tipoVenta === TIPO_VENTA_PESO_MANUAL
            ? `${Number(item.pesoKg).toFixed(3)} Kg x ${formatMoneda(item.precioPorKg)}`
            : `${item.cantidad} x ${formatMoneda(item.precio)}`}
        </div>
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => actualizarCantidad(item.id, item.cantidad - 1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.08] text-xs text-white hover:bg-white/15"
          aria-label={`Restar ${item.nombre}`}
        >
          <FaMinus />
        </button>
        <div className="min-w-[28px] text-center text-base font-black text-white">
          {item.cantidad}
        </div>
        <button
          type="button"
          onClick={() => actualizarCantidad(item.id, item.cantidad + 1)}
          disabled={false}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.08] text-xs text-white hover:bg-white/15 disabled:opacity-40"
          aria-label={`Sumar ${item.nombre}`}
        >
          <FaPlus />
        </button>
        <div className="min-w-[74px] text-right text-sm font-black text-white tabular-nums">
          {formatLineaMoneda(item, lineaCarritoTotal(item))}
        </div>
      </div>
    </div>
  ));

  const tablaCarritoIzquierda =
    carrito.length === 0 ? null : (
      <table className="w-full min-w-0 table-fixed border-collapse text-left">
        <thead className="sticky top-0 z-[1] border-b border-white/[0.1] bg-e0e3-surface/98 text-[11px] font-black uppercase tracking-wider text-e0e3-muted">
          <tr>
            <th className="w-8 px-1 py-2">No</th>
            <th className="w-[4.5rem] px-1 py-2">Cód.</th>
            <th className="px-2 py-2">Ítem</th>
            <th className="w-[6.5rem] py-2 pr-1 text-center">Cant.</th>
            <th className="w-[5rem] py-2 pr-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {carrito.map((item, idx) => (
            <tr
              key={item.id}
              className={`align-middle text-xs ${idx % 2 === 0 ? 'bg-white/[0.03]' : 'bg-black/[0.18]'}`}
            >
              <td className="px-1 py-2 font-black text-white">{idx + 1}</td>
              <td className="max-w-[4.5rem] truncate px-1 py-2 font-mono text-[10px] text-e0e3-accentSoft">
                {item.codigo || '–'}
              </td>
              <td className="min-w-0 px-2 py-2">
                <button type="button" onClick={() => setMostrarCarritoCompleto(true)} className="w-full min-w-0 text-left">
                  <div className="truncate font-semibold leading-tight text-white">{item.nombre}</div>
                  <div className="truncate text-[11px] tabular-nums text-e0e3-muted">
                    {item.tipoVenta === TIPO_VENTA_PESO_MANUAL
                      ? `${Number(item.pesoKg).toFixed(3)} Kg x ${formatMoneda(item.precioPorKg)}`
                      : `@ ${formatMoneda(item.precio)}`}
                  </div>
                </button>
              </td>
              <td className="py-2 pr-0">
                <div className="flex items-center justify-center gap-0.5 tabular-nums">
                  <button
                    type="button"
                    onClick={() => actualizarCantidad(item.id, item.cantidad - 1)}
                    className="flex h-9 w-8 items-center justify-center rounded-md bg-white/[0.1] text-white hover:bg-white/15"
                  >
                    <FaMinus />
                  </button>
                  <span className="min-w-[1.75rem] text-center text-sm font-black">{item.cantidad}</span>
                  <button
                    type="button"
                    onClick={() => actualizarCantidad(item.id, item.cantidad + 1)}
                    className="flex h-9 w-8 items-center justify-center rounded-md bg-white/[0.1] text-white hover:bg-white/15"
                  >
                    <FaPlus />
                  </button>
                </div>
              </td>
              <td className="py-2 pr-2 text-right text-sm font-black tabular-nums text-white">
                {formatLineaMoneda(item, lineaCarritoTotal(item))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  const pieTotalCarrito =
    carrito.length > 0 ? (
      <div className="flex justify-between rounded-b-xl border-t border-white/[0.08] bg-black/55 px-3 py-2 text-white tabular-nums">
        <span className="text-sm font-black uppercase tracking-wide text-e0e3-muted">Subtotal</span>
        <span className="text-xl font-black text-e0e3-accent">{formatMoneda(total)}</span>
      </div>
    ) : null;

  /** Resumen TOTAL / RECIBIDO / VUELTO; `dense` panel apaisado; `tablet` tipografía POS. */
  const bloqueTotalesVentas = ({ dense, tablet } = {}) => (
    <div
      className={`shrink-0 rounded-xl border border-white/[0.14] bg-[#151518] shadow-inner backdrop-blur-sm ${
        dense ? 'space-y-1 border-white/[0.1] px-2.5 py-2' : 'space-y-1.5 px-2.5 py-2 sm:px-3 sm:py-2.5'
      }`}
    >
      <div
        className={`flex items-end justify-between gap-2 border-b border-white/[0.1] ${
          dense ? 'min-h-[2rem] pb-1' : 'min-h-[2.85rem] pb-2'
        }`}
      >
        <span className="shrink-0 text-[11px] font-black uppercase tracking-widest text-e0e3-muted">Total</span>
        <span
          className="min-w-0 truncate text-right font-extrabold tabular-nums leading-none text-white"
          style={{
            fontSize: tablet
              ? 'clamp(1.35rem, 3.2vmin, 2rem)'
              : dense
                ? 'clamp(1.15rem, 2.6vmin, 1.85rem)'
                : 'clamp(1.35rem, 3.25vmin, 2.95rem)'
          }}
        >
          {formatMoneda(total)}
        </span>
      </div>
      <div className={`flex items-center justify-between gap-3 font-bold text-e0e3-muted ${dense ? 'text-[11px]' : 'text-[11px] sm:text-xs'}`}>
        <span className="uppercase tracking-wide">
          {cobroEnEfectivo ? 'Recibido' : cobroEnCredito ? 'Anticipo' : 'Importe pagado'}
        </span>
        <span
          className="truncate text-right font-black tabular-nums text-white"
          style={{ fontSize: tablet ? 'clamp(1rem, 2.2vmin, 1.25rem)' : dense ? 'clamp(0.85rem, 1.9vmin, 1.05rem)' : 'clamp(0.92rem, 2.05vmin, 1.125rem)' }}
        >
          {formatMoneda(
            cobroEnEfectivo
              ? montoRecibidoNum
              : cobroEnCredito
                ? resumenPagoCajero.monto_pagado
                : total
          )}
        </span>
      </div>
      {cobroEnCredito && resumenPagoCajero.saldo_pendiente > 0 && (
        <div className={`flex items-center justify-between gap-3 font-bold text-e0e3-warn ${dense ? 'text-[11px]' : 'text-[11px] sm:text-xs'}`}>
          <span className="uppercase tracking-wide">Saldo CC</span>
          <span className="truncate text-right font-black tabular-nums">
            {formatMoneda(resumenPagoCajero.saldo_pendiente)}
          </span>
        </div>
      )}
      {cobroEnEfectivo && (
      <div className={`flex items-end justify-between gap-2 border-t border-white/[0.1] ${dense ? 'min-h-[1.85rem] pt-1' : 'min-h-[2.25rem] pt-1'}`}>
        <span className="shrink-0 text-[11px] font-black uppercase tracking-wider text-e0e3-muted">Vuelto</span>
        <span
          className="truncate font-black tabular-nums leading-none text-e0e3-ok"
          style={{
            fontSize: tablet
              ? 'clamp(1.1rem, 2.5vmin, 1.5rem)'
              : dense
                ? 'clamp(0.95rem, 2.05vmin, 1.42rem)'
                : 'clamp(1.1rem, 2.85vmin, 2.05rem)'
          }}
        >
          {formatMoneda(vuelto)}
        </span>
      </div>
      )}
    </div>
  );

  /** Panel persistente: cliente activo de la venta (solo presentación). */
  const bloqueClienteActualCajero = ({ compact: dense = false } = {}) => {
    const esConsumidorFinal = !clienteCajeroConId;
    const tipoVentaLabel = cobroEnCredito ? 'Cuenta corriente' : 'Contado';
    const docCliente =
      clienteSeleccionado?.dni_cuit ||
      clienteSeleccionado?.cuit ||
      clienteSeleccionado?.documento ||
      '';
    const telCliente = clienteSeleccionado?.telefono || '';

    return (
      <div
        className={`shrink-0 rounded-xl border shadow-card ${
          dense ? 'p-2' : 'p-2.5 sm:p-3'
        } ${
          esConsumidorFinal
            ? 'border-white/[0.08] bg-e0e3-surface/95'
            : 'border-e0e3-accent/40 bg-gradient-to-br from-e0e3-surface/98 via-[#151821] to-[#12141a] ring-1 ring-e0e3-accent/20'
        }`}
      >
        <div className={`flex items-start justify-between gap-2 ${dense ? 'mb-1.5' : 'mb-2'}`}>
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <div
              className={`flex shrink-0 items-center justify-center rounded-lg ${
                dense ? 'h-9 w-9' : 'h-10 w-10'
              } ${
                esConsumidorFinal
                  ? 'bg-white/[0.06] text-e0e3-muted'
                  : 'bg-e0e3-accent/15 text-e0e3-accentSoft'
              }`}
            >
              <FaUser className={dense ? 'text-sm' : 'text-base'} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-e0e3-muted">
                {esConsumidorFinal ? 'Mostrador' : 'Cliente actual'}
              </div>
              <div
                className={`truncate font-black leading-tight text-white ${
                  dense ? 'text-sm' : 'text-base sm:text-lg'
                }`}
              >
                {nombreCliente(clienteSeleccionado)}
              </div>
              {!esConsumidorFinal && (docCliente || telCliente) && (
                <div className={`mt-0.5 space-y-0.5 ${dense ? 'text-[10px]' : 'text-[11px]'}`}>
                  {docCliente && (
                    <div className="truncate font-semibold text-e0e3-muted">
                      Doc/CUIT: <span className="text-white/90">{docCliente}</span>
                    </div>
                  )}
                  {telCliente && (
                    <div className="truncate font-semibold text-e0e3-muted">
                      Tel: <span className="text-white/90">{telCliente}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <span
            className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
              cobroEnCredito
                ? 'border border-amber-400/40 bg-amber-500/15 text-amber-300'
                : 'border border-white/[0.1] bg-white/[0.06] text-e0e3-muted'
            }`}
          >
            {tipoVentaLabel}
          </span>
        </div>

        {esConsumidorFinal && !dense && (
          <p className="mb-2 text-[11px] font-semibold text-e0e3-muted">
            Venta de contado · asigná un cliente para cuenta corriente
          </p>
        )}

        {!esConsumidorFinal && limiteCreditoCajero && (
          <div
            className={`mb-2 grid grid-cols-2 gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] ${
              dense ? 'px-2 py-1.5 text-[10px]' : 'px-2.5 py-2 text-[11px]'
            }`}
          >
            <div>
              <div className="font-semibold uppercase tracking-wide text-e0e3-muted">Límite</div>
              <div className="font-black text-white">{formatMoneda(limiteCreditoCajero.limite)}</div>
            </div>
            <div className="text-right">
              <div className="font-semibold uppercase tracking-wide text-e0e3-muted">Disponible</div>
              <div
                className={`font-black ${
                  limiteCreditoCajero.disponible > 0 ? 'text-emerald-300' : 'text-red-300'
                }`}
              >
                {formatMoneda(limiteCreditoCajero.disponible)}
              </div>
            </div>
          </div>
        )}

        {!esConsumidorFinal && deudaTotalCliente > 0 && cajaClientesHabilitado && (
          <button
            type="button"
            onClick={() => setMostrarDeudaCliente(true)}
            className={`mb-2 w-full rounded-lg border border-e0e3-warn/35 bg-e0e3-warn/10 text-left font-semibold text-e0e3-warn ${
              dense ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[11px]'
            }`}
          >
            Deuda registrada: {formatMoneda(deudaTotalCliente)}
          </button>
        )}

        <div className={`grid gap-1.5 ${clienteCajeroConId ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <button
            type="button"
            onClick={() => setMostrarClientes(true)}
            className={`min-h-10 rounded-lg border border-e0e3-accent/35 bg-e0e3-accent/10 font-black text-e0e3-accentSoft hover:bg-e0e3-accent/20 active:scale-[0.99] ${
              dense ? 'px-2 text-[10px]' : 'px-2.5 text-xs'
            }`}
          >
            Cambiar cliente
          </button>
          {clienteCajeroConId && (
            <button
              type="button"
              onClick={seleccionarConsumidorFinal}
              className={`min-h-10 rounded-lg border border-white/[0.12] bg-white/[0.06] font-black text-e0e3-muted hover:bg-white/[0.1] hover:text-white active:scale-[0.99] ${
                dense ? 'px-2 text-[10px]' : 'px-2.5 text-xs'
              }`}
            >
              <FaUserMinus className="mr-1 inline-block" />
              Quitar cliente
            </button>
          )}
        </div>
      </div>
    );
  };

  const bloqueBusquedaPos = ({ landscape = false } = {}) => (
    <div className={`cajero-pos-search-block rounded-xl border border-e0e3-accent/40 bg-black/35 shadow-card ${landscape ? 'p-2' : 'p-2 sm:p-2.5'}`}>
      <div className="relative">
        <FaSearch className={`pointer-events-none absolute left-3 text-e0e3-accent ${landscape ? 'top-3 text-sm' : compact ? 'top-2.5' : 'top-3.5 sm:left-4'}`} />
        <input
          id="pos-busqueda-producto"
          ref={searchInputRef}
          type="text"
          value={busqueda}
          onChange={(event) => setBusqueda(event.target.value)}
          onKeyDown={(event) => {
            if (activacionMaster.open) return;
            if (event.key === 'Enter') {
              event.preventDefault();
              buscarPorCodigoExacto();
            }
          }}
          disabled={activacionMaster.open || !sucursalVenta || procesandoVenta || flujoPostVentaActivo}
          placeholder={landscape ? 'Escanear código o buscar…' : 'Escanear o buscar código / nombre...'}
          autoComplete="off"
          className={`w-full min-w-0 rounded-xl border-2 border-e0e3-accent/50 bg-[#141416] pr-3 font-semibold text-white placeholder:text-e0e3-muted/80 shadow-inner focus:border-e0e3-accent focus:outline-none focus:ring-4 focus:ring-e0e3-accent/20 ${
            landscape
              ? 'min-h-12 py-2.5 pl-10 text-base'
              : compact
                ? 'py-2 pl-10 text-base'
                : 'py-3 pl-10 text-lg sm:pl-11 sm:pr-4'
          }`}
          inputMode="search"
        />
      </div>
      <div className={`flex gap-2 ${landscape ? 'mt-2' : 'mt-2 sm:mt-2.5'}`}>
        <button
          type="button"
          onClick={buscarPorCodigoExacto}
          disabled={!busqueda.trim() || !sucursalVenta || procesandoVenta || flujoPostVentaActivo}
          className={`flex min-w-0 flex-1 items-center justify-center rounded-xl bg-e0e3-accent font-black text-black shadow-[0_8px_24px_rgba(30,144,255,0.35)] hover:brightness-110 disabled:bg-white/10 disabled:text-e0e3-muted disabled:shadow-none ${
            landscape ? 'min-h-11 text-sm' : compact ? 'min-h-10 text-xs' : 'min-h-11 text-sm sm:text-base'
          }`}
        >
          <FaBarcode className="mr-2 shrink-0" />
          {landscape ? 'Agregar' : 'Agregar por código'}
        </button>
      </div>
      {!landscape && (
        <p className="mt-2 text-[11px] leading-snug text-e0e3-muted">
          Balanza Kretz (prefijo 20): PLU + peso en kg automático.
        </p>
      )}
    </div>
  );

  /**
   * Panel derecho POS tablet: total, métodos, efectivo, cobrar fijo al pie.
   */
  const panelCheckoutPosLandscape =
    splitLandscapeLayout && (
      <section
        aria-label="Cobro"
        data-pos-checkout="1"
        className="cajero-pos-checkout-pane rounded-xl border border-white/[0.08] bg-e0e3-surface/98 p-2.5 shadow-inner ring-1 ring-black/25"
      >
        <div className="cajero-pos-checkout-scroll scrollbar-thin space-y-2.5">
          {bloqueTotalesVentas({ dense: true, tablet: true })}

          <div className={`grid shrink-0 gap-1.5 ${mostrarMetodoCuentaCorriente ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <button
              type="button"
              onClick={() => seleccionarMetodoPago('efectivo')}
              className={`min-h-12 rounded-lg px-1 text-center text-xs font-black uppercase leading-tight ring-1 ${
                metodoPagoActivo === 'efectivo' ? 'bg-e0e3-ok text-white ring-emerald-300/50' : 'bg-e0e3-ok/90 text-white ring-black/35 hover:brightness-110'
              }`}
            >
              <FaMoneyBillWave className="mx-auto mb-0.5 text-base" />
              Efectivo
            </button>
            <button
              type="button"
              onClick={() => seleccionarMetodoPago('tarjeta')}
              className={`min-h-12 rounded-lg px-1 text-center text-xs font-black uppercase leading-tight ring-1 ${
                metodoPagoActivo === 'tarjeta' ? 'bg-[#1E90FF] text-white ring-sky-200/55' : 'bg-[#1E90FF]/95 text-white ring-black/30 hover:brightness-110'
              }`}
            >
              <FaCreditCard className="mx-auto mb-0.5 text-base" />
              Tarjeta
            </button>
            <button
              type="button"
              onClick={() => seleccionarMetodoPago('MercadoPago')}
              className={`min-h-12 rounded-lg px-1 text-center text-[10px] font-black uppercase leading-tight ring-1 ${
                metodoPagoActivo === 'MercadoPago' ? 'bg-purple-600 text-white ring-purple-300/50' : 'bg-purple-700 text-white ring-black/35 hover:brightness-105'
              }`}
            >
              <FaMobileAlt className="mx-auto mb-0.5 text-base" />
              {BILLETERA_VIRTUAL_LABEL}
            </button>
            {mostrarMetodoCuentaCorriente && (
              <button
                type="button"
                onClick={() => seleccionarMetodoPago('credito')}
                className={`min-h-12 rounded-lg px-1 text-center text-[10px] font-black uppercase leading-tight ring-1 ${
                  metodoPagoActivo === 'credito'
                    ? 'bg-amber-500 text-black ring-amber-200/60'
                    : 'bg-amber-500/90 text-black ring-black/30 hover:brightness-110'
                }`}
              >
                <FaClock className="mx-auto mb-0.5 text-base" />
                Cuenta corriente
              </button>
            )}
          </div>

          {(cobroEnEfectivo || cobroEnCredito) && (
            <div data-pos-busqueda-pausada="1" className="shrink-0 space-y-1.5">
              <label className="block text-[10px] font-black uppercase tracking-wider text-e0e3-muted">
                {cobroEnCredito ? 'Anticipo $ (opcional)' : 'Recibido $'}
              </label>
              <input
                type="number"
                value={montoRecibido}
                onChange={(event) => setMontoRecibido(event.target.value)}
                placeholder="0.00"
                className="min-h-12 w-full min-w-0 rounded-lg border-2 border-white/[0.12] bg-black/55 px-3 text-lg font-black tabular-nums text-white focus:border-e0e3-accent focus:outline-none"
                inputMode="decimal"
                disabled={carrito.length === 0 || procesandoVenta}
              />
              {cobroEnEfectivo && (
              <div className="grid grid-cols-2 gap-1.5">
                {botonesMontoRapido.map((valor) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => setMontoRecibido(String(valor))}
                    className="min-h-10 rounded-lg border border-white/[0.1] bg-e0e3-surfaceHover text-xs font-black tabular-nums text-white hover:bg-white/10"
                  >
                    {formatMoneda(valor)}
                  </button>
                ))}
              </div>
              )}
            </div>
          )}

          <div className="grid shrink-0 grid-cols-2 gap-1.5">
            {cajaClientesHabilitado && (
              <button
                type="button"
                onClick={() => setMostrarClientes(true)}
                className="min-h-10 rounded-lg bg-white/[0.08] px-2 text-[10px] font-black uppercase text-e0e3-muted hover:bg-white/[0.14]"
              >
                Cliente
              </button>
            )}
            <button
              type="button"
              onClick={limpiarVenta}
              disabled={carrito.length === 0 || procesandoVenta}
              className="min-h-10 rounded-lg bg-amber-500 px-2 text-[10px] font-black uppercase text-black hover:bg-amber-400 disabled:opacity-35"
            >
              Limpiar
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={finalizarVenta}
          disabled={carrito.length === 0 || cobroBloqueado || !sucursalVenta}
          className="cajero-pos-cobrar-btn mt-2 min-h-[4.25rem] w-full min-w-0 rounded-xl border-2 border-[#1E90FF] bg-[#1E90FF] py-3 text-xl font-black uppercase tracking-[0.12em] text-black shadow-[0_10px_28px_rgba(30,144,255,0.45)] hover:brightness-110 disabled:border-white/[0.12] disabled:bg-white/10 disabled:text-e0e3-muted disabled:shadow-none"
        >
          {procesandoVenta ? (
            <span className="inline-flex items-center justify-center gap-2">
              <FaSyncAlt className="animate-spin" aria-hidden />
              Procesando…
            </span>
          ) : (
            'Cobrar'
          )}
        </button>
      </section>
    );
  const tarjetaCarritoLandscape = (
    <div className="cajero-pos-cart-pane rounded-xl border border-white/[0.06] bg-e0e3-surface/95 p-2 shadow-card">
      <div className="mb-1.5 flex shrink-0 items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base font-black text-white">Carrito</h2>
          <p className="text-[11px] font-semibold text-e0e3-muted">
            {carrito.reduce((sum, item) => sum + item.cantidad, 0)} ítems · {carrito.length} productos
          </p>
        </div>
        <button
          type="button"
          onClick={() => setMostrarCarritoCompleto(true)}
          disabled={carrito.length === 0}
          className="shrink-0 rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 py-1.5 text-xs font-black text-e0e3-accent hover:bg-white/10 disabled:opacity-40"
        >
          Expandir
        </button>
      </div>

      {carrito.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/25 p-4 text-center text-sm font-semibold text-e0e3-muted">
          Escaneá o buscá un producto para comenzar.
        </div>
      ) : (
        <div className="scrollbar-thin min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain rounded-lg border border-white/[0.06] bg-black/20">
          {tablaCarritoIzquierda}
        </div>
      )}
    </div>
  );

  const panelCobroResumen = !splitLandscapeLayout ? (
    <>
      <div
        data-pos-checkout="1"
        className={`flex flex-col rounded-xl border border-white/[0.06] bg-e0e3-surface shadow-card ${
          compact ? 'p-2' : 'p-4'
        }`}
      >
        {bloqueTotalesVentas()}

        <div className={`mt-4 grid ${mostrarMetodoCuentaCorriente ? 'grid-cols-2' : 'grid-cols-3'} ${compact ? 'gap-2' : 'gap-3'}`}>
          <button
            type="button"
            onClick={() => seleccionarMetodoPago('efectivo')}
            className={`rounded-xl border font-black ring-2 ring-transparent transition ${
              compact ? 'min-h-12 py-2 text-xs' : 'min-h-[64px] text-lg'
            } ${
              metodoPagoActivo === 'efectivo'
                ? 'border-e0e3-ok bg-e0e3-ok text-white ring-offset-2 ring-offset-e0e3-surface ring-e0e3-ok/50'
                : 'border-white/[0.06] bg-e0e3-surfaceHover text-e0e3-muted hover:border-white/12'
            }`}
          >
            <FaMoneyBillWave className="mx-auto mb-0.5 text-sm sm:text-base" />
            Efectivo
          </button>
          <button
            type="button"
            onClick={() => seleccionarMetodoPago('tarjeta')}
            className={`rounded-xl border font-black ring-2 ring-transparent transition ${
              compact ? 'min-h-12 py-2 text-xs' : 'min-h-[64px] text-lg'
            } ${
              metodoPagoActivo === 'tarjeta'
                ? 'border-e0e3-accent bg-e0e3-accent text-white ring-offset-2 ring-offset-e0e3-surface ring-e0e3-accent/50'
                : 'border-white/[0.06] bg-e0e3-surfaceHover text-e0e3-muted hover:border-white/12'
            }`}
          >
            <FaCreditCard className="mx-auto mb-0.5 text-sm sm:text-base" />
            Tarjeta
          </button>
          <button
            type="button"
            onClick={() => seleccionarMetodoPago('MercadoPago')}
            className={`rounded-xl border font-black ring-2 ring-transparent transition ${
              compact ? 'min-h-12 py-2 text-[10px]' : 'min-h-[64px] text-base'
            } ${
              metodoPagoActivo === 'MercadoPago'
                ? 'border-purple-400 bg-purple-600 text-white ring-offset-2 ring-offset-e0e3-surface ring-purple-400/45'
                : 'border-white/[0.06] bg-e0e3-surfaceHover text-e0e3-muted hover:border-white/12'
            }`}
          >
            <FaMobileAlt className="mx-auto mb-0.5 text-sm sm:text-base" />
            {BILLETERA_VIRTUAL_LABEL}
          </button>
          {mostrarMetodoCuentaCorriente && (
            <button
              type="button"
              onClick={() => seleccionarMetodoPago('credito')}
              className={`rounded-xl border font-black ring-2 ring-transparent transition ${
                compact ? 'min-h-12 py-2 text-[10px]' : 'min-h-[64px] text-sm'
              } ${
                metodoPagoActivo === 'credito'
                  ? 'border-amber-400 bg-amber-500 text-black ring-offset-2 ring-offset-e0e3-surface ring-amber-400/50'
                  : 'border-white/[0.06] bg-e0e3-surfaceHover text-e0e3-muted hover:border-white/12'
              }`}
            >
              <FaClock className="mx-auto mb-0.5 text-sm sm:text-base" />
              Cuenta corriente
            </button>
          )}
        </div>

        {(cobroEnEfectivo || cobroEnCredito) && (
          <div data-pos-busqueda-pausada="1" className={compact ? 'mt-3' : 'mt-4'}>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-e0e3-muted">
              {cobroEnCredito ? 'Anticipo (opcional)' : 'Dinero recibido'}
            </label>
            <input
              type="number"
              value={montoRecibido}
              onChange={(event) => setMontoRecibido(event.target.value)}
              placeholder="0.00"
              className={`w-full rounded-xl border-2 border-white/[0.1] bg-black/35 px-3 font-black tabular-nums text-white placeholder:text-e0e3-muted focus:border-e0e3-accent focus:outline-none focus:ring-2 focus:ring-e0e3-accent/30 ${
                compact ? 'py-2 text-2xl leading-tight' : 'py-3 text-3xl'
              }`}
              inputMode="decimal"
              disabled={carrito.length === 0 || procesandoVenta}
            />

            {cobroEnEfectivo && (
            <div className={`mt-3 grid gap-1.5 ${botonesMontoRapido.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'}`}>
              {botonesMontoRapido.map((valor) => (
                <button
                  key={valor}
                  type="button"
                  onClick={() => setMontoRecibido(String(valor))}
                  className={`rounded-lg border border-white/[0.08] bg-e0e3-surfaceHover font-black text-white tabular-nums hover:bg-white/10 active:scale-[0.98] ${
                    compact ? 'min-h-[40px] py-2 text-xs' : 'px-3 py-3 text-lg'
                  }`}
                >
                  {formatMoneda(valor)}
                </button>
              ))}
            </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={finalizarVenta}
          disabled={carrito.length === 0 || cobroBloqueado || !sucursalVenta}
          className={`mt-4 flex w-full items-center justify-center rounded-xl bg-e0e3-ok font-black text-black shadow-[0_8px_24px_rgba(34,197,94,0.35)] transition hover:brightness-110 disabled:bg-white/15 disabled:text-e0e3-muted disabled:shadow-none ${
            compact ? 'min-h-[54px] text-lg' : 'min-h-[72px] text-2xl'
          }`}
        >
          {procesandoVenta ? (
            <>
              <FaSyncAlt className="mr-2 shrink-0 animate-spin" aria-hidden />
              Cobrando...
            </>
          ) : (
            <>
              <FaCheck className="mr-2 shrink-0" />
              Cobrar
            </>
          )}
        </button>

        <button
          type="button"
          onClick={limpiarVenta}
          disabled={carrito.length === 0 || procesandoVenta}
          className={`w-full rounded-xl border border-white/[0.1] bg-white/[0.06] font-bold text-e0e3-muted hover:bg-white/[0.1] disabled:opacity-40 ${
            compact ? 'mt-2 min-h-10 text-xs' : 'mt-3 min-h-[52px] text-base'
          }`}
        >
          Limpiar venta
        </button>
      </div>

      {ultimaVenta && (
        <div className={`rounded-xl border border-e0e3-ok/35 bg-e0e3-ok/10 text-e0e3-foreground ${compact ? 'p-3' : 'p-4'}`}>
          <div className="text-lg font-black text-e0e3-ok">Venta registrada</div>
          <div className="text-sm text-e0e3-muted">
            ID: {ultimaVenta.id || 'sin ID'} · Total {formatMoneda(ultimaVenta.total)} · Vuelto {formatMoneda(ultimaVenta.vuelto)}
          </div>
        </div>
      )}
    </>
  ) : null;

  const tarjetaCarritoPortrait = (
    <div className="rounded-xl border border-white/[0.06] bg-e0e3-surface/95 p-2 shadow-card sm:p-2.5">
      <div className="mb-2 flex items-center justify-between sm:mb-2">
        <div>
          <h2 className={`font-black text-white ${compact ? 'text-base' : 'text-lg'}`}>Carrito</h2>
          <p className="text-xs font-semibold text-e0e3-muted">
            {carrito.reduce((sum, item) => sum + item.cantidad, 0)} unidades · {carrito.length} productos
          </p>
        </div>
        <button
          type="button"
          onClick={() => setMostrarCarritoCompleto(true)}
          disabled={carrito.length === 0}
          className="rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 py-1.5 text-xs font-black text-e0e3-accent hover:bg-white/10 disabled:opacity-40"
        >
          Ver carrito
        </button>
      </div>

      {carrito.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 bg-black/25 p-4 text-center text-sm font-semibold text-e0e3-muted">
          Escaneá o buscá un producto para comenzar.
        </div>
      ) : (
        <div className={`divide-y divide-white/[0.06] rounded-lg border border-white/[0.06] bg-black/20 ${carritoMaxClass} overflow-y-auto`}>
          {lineasCarrito}
          <div className="sticky bottom-0 flex justify-between border-t border-white/[0.08] bg-black/65 px-3 py-2 text-white tabular-nums backdrop-blur-sm">
            <span className="text-xs font-black uppercase tracking-wide text-e0e3-muted">Subtotal</span>
            <span className="text-lg font-black text-e0e3-accent">{formatMoneda(total)}</span>
          </div>
        </div>
      )}
    </div>
  );

  const carritoCobroUltima = (
    <>
      {tarjetaCarritoPortrait}
      {panelCobroResumen}
    </>
  );

  const claseBtnAccion = 'flex min-h-11 shrink-0 items-center justify-center gap-1 rounded-lg px-2.5 text-[11px] font-black uppercase tracking-wide disabled:opacity-50';

  const accionesRapidasCajero = (
    <div className="cajero-pos-tablet-actions flex min-w-0 shrink-0 items-center gap-1">
      <CajeroHotkeyHint compact />
      {fullscreenDisponible && (
        <button
          type="button"
          onClick={alternarPantallaCompleta}
          className={`${claseBtnAccion} bg-white/10 text-white ring-1 ring-white/15 hover:bg-white/15`}
          title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
        >
          {isFullscreen ? <FaCompress className="text-xs" /> : <FaExpand className="text-xs" />}
          <span>{isFullscreen ? 'Salir' : 'Pantalla'}</span>
        </button>
      )}
      <button
        type="button"
        onClick={() => refreshCatalogoCajero({ force: true })}
        disabled={!sucursalVenta || catalogRefreshing}
        className={`${claseBtnAccion} bg-white/10 text-e0e3-accentSoft ring-1 ring-e0e3-accent/25 hover:bg-white/15`}
        title="Forzar actualización del catálogo"
      >
        <FaSyncAlt className={catalogRefreshing ? 'animate-spin text-xs' : 'text-xs'} />
        <span>Catálogo</span>
      </button>
      <button
        type="button"
        onClick={actualizarAppCaja}
        disabled={actualizandoApp}
        className={`${claseBtnAccion} bg-white/10 text-white ring-1 ring-white/15 hover:bg-white/15`}
        title="Recargar interfaz y verificar APK"
      >
        <FaSyncAlt className={actualizandoApp ? 'animate-spin text-xs' : 'text-xs'} />
        <span>Actualizar</span>
      </button>
      <button
        type="button"
        onClick={abrirModalCierreDesdeMostrador}
        className={`${claseBtnAccion} bg-teal-600/90 text-white ring-1 ring-teal-400/30 hover:bg-teal-500`}
        title={ENABLE_CAJERO_CIERRE_DIARIO ? 'Cierre diario de caja' : 'Cierre diario próximamente'}
      >
        <FaCashRegister className="text-xs" />
        <span>Cierre</span>
      </button>
    </div>
  );

  const bannerActualizacionApk = apkUpdateBanner && !apkBannerDismissed && isNativeCajaApk() ? (
    <div className="flex shrink-0 items-center justify-between gap-2 rounded-lg border border-e0e3-accent/35 bg-e0e3-accent/10 px-2.5 py-1.5 text-[11px] font-semibold text-e0e3-accentSoft">
      <span className="min-w-0 truncate">
        Hay una actualización disponible (v{apkUpdateBanner.serverName})
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={actualizarAppCaja}
          disabled={actualizandoApp}
          className="min-h-9 rounded-md bg-e0e3-accent px-2.5 text-[10px] font-black uppercase text-black hover:brightness-110 disabled:opacity-60"
        >
          Actualizar
        </button>
        <button
          type="button"
          onClick={() => setApkBannerDismissed(true)}
          className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/15"
          title="Ocultar aviso"
        >
          <FaTimes className="text-xs" />
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        className={`flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col ${
          landscapeTablet ? 'overflow-hidden gap-1' : 'scrollbar-thin gap-1.5 overflow-y-auto overscroll-y-contain sm:gap-2'
        }`}
      >
        {splitLandscapeLayout ? (
          <header className="cajero-pos-tablet-header shrink-0 rounded-lg border border-white/[0.08] bg-gradient-to-r from-e0e3-shell to-e0e3-bg px-2 py-1.5 shadow-card">
            <h1 className="truncate text-sm font-black text-white">Mostrador · 0E3</h1>
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${isOnline ? 'bg-e0e3-ok' : 'bg-e0e3-warn'}`}
              title={isOnline ? 'En línea' : 'Sin conexión'}
            />
            {catalogUsingSaved && catalogReady && (
              <span className="shrink-0 rounded bg-e0e3-warn/15 px-1.5 py-0.5 text-[9px] font-bold text-e0e3-warn">Cache</span>
            )}
            {sucursalesDisponibles?.length > 1 && (
              <div className="hidden min-w-0 flex-1 flex-wrap items-center gap-1 overflow-hidden md:flex">
                {sucursalesDisponibles.map((sucursal) => (
                  <button
                    key={sucursal.id}
                    type="button"
                    onClick={() => setSucursalVenta(sucursal.id)}
                    className={`max-w-[8rem] truncate rounded-md px-2 py-0.5 text-[10px] font-black ${
                      sucursalVenta === sucursal.id
                        ? 'bg-e0e3-accent text-white'
                        : 'bg-white/10 text-e0e3-muted hover:bg-white/15'
                    }`}
                  >
                    {sucursal.nombre}
                  </button>
                ))}
              </div>
            )}
            <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1">
              <div className="hidden min-w-0 md:flex">
                <UserAvatar user={currentUser} size="xs" showMeta />
              </div>
              {accionesRapidasCajero}
              <button
                type="button"
                onClick={cerrarSesion}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/15"
                title="Salir"
              >
                <FaSignOutAlt className="text-sm" />
              </button>
            </div>
          </header>
        ) : (
        <>
        <div
          className={`shrink-0 rounded-xl border border-white/[0.08] bg-gradient-to-r from-e0e3-shell via-[#16181e] to-e0e3-bg shadow-card ${
            compact ? 'p-2.5' : 'p-3.5'
          }`}
        >
        <div className={`flex items-center justify-between gap-2 sm:gap-3`}>
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <h1
                className={`font-black leading-tight tracking-tight text-white ${compact ? 'text-lg' : 'text-2xl'}`}
              >
                Mostrador · 0E3
              </h1>
              <span className="hidden rounded-md border border-white/10 bg-black/35 px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wide text-e0e3-accent lg:inline-flex tabular-nums">
                {formatMoneda(total)}
              </span>
            </div>
            <p className={`text-e0e3-muted ${compact ? 'text-[11px]' : 'text-xs'}`}>
              {getUserFullName(currentUser)} · Scanner / código
            </p>
          </div>
          <div className={`flex shrink-0 items-center gap-1.5 sm:gap-3`}>
            <div className="text-right">
              <div className={`uppercase tracking-wider text-e0e3-muted ${compact ? 'text-[10px]' : 'text-[11px]'}`}>Total</div>
              <div
                className={`font-black tabular-nums text-e0e3-accentSoft leading-none ${
                  compact ? 'text-xl' : 'text-3xl'
                }`}
              >
                {formatMoneda(total)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => refreshCatalogoCajero({ force: true })}
              disabled={!sucursalVenta || catalogRefreshing}
              className={`flex items-center rounded-xl bg-white/10 font-black text-e0e3-accentSoft ring-1 ring-e0e3-accent/25 hover:bg-white/15 disabled:opacity-60 ${
                compact
                  ? 'min-h-9 gap-1.5 px-2 text-xs'
                  : 'min-h-[46px] gap-2 px-3 text-sm sm:gap-2'
              }`}
              title={`Actualizar catálogo local${catalogCount > 0 ? ` (${catalogCount} productos)` : ''}`}
            >
              <FaSyncAlt className={catalogRefreshing ? 'animate-spin' : ''} />
              {!compact ? 'Catálogo' : ''}
            </button>
            <button
              type="button"
              onClick={actualizarAppCaja}
              disabled={actualizandoApp}
              className={`flex items-center rounded-xl bg-white/15 font-black text-white ring-1 ring-white/20 hover:bg-white/20 disabled:opacity-60 ${
                compact
                  ? 'min-h-9 gap-1.5 px-2 text-xs'
                  : 'min-h-[46px] gap-2 px-3 text-sm sm:gap-2'
              }`}
              title="Actualizar configuracion y datos de caja"
            >
              <FaSyncAlt className={actualizandoApp ? 'animate-spin' : ''} />
              {!compact ? 'Actualizar' : ''}
            </button>
            <button
              type="button"
              onClick={abrirModalCierreDesdeMostrador}
              className={`flex items-center rounded-xl bg-teal-600/90 font-black text-white ring-1 ring-teal-400/40 hover:bg-teal-500 ${
                compact
                  ? 'min-h-9 gap-1.5 px-2 text-[10px]'
                  : 'min-h-[46px] gap-2 px-3 text-sm sm:gap-2'
              }`}
              title={ENABLE_CAJERO_CIERRE_DIARIO ? 'Cierre diario de caja · arqueo' : 'Cierre diario: proximamente'}
            >
              <FaCashRegister />
              {!compact ? 'Cierre' : ''}
            </button>
            <CajeroHotkeyHint compact={compact} />
            {fullscreenDisponible && (
            <button
              type="button"
              onClick={alternarPantallaCompleta}
              className={`flex items-center rounded-xl bg-white/15 font-black text-white ring-1 ring-white/20 hover:bg-white/20 ${
                compact
                  ? 'min-h-9 gap-1 px-2 text-xs'
                  : 'min-h-[46px] gap-2 px-3 text-sm'
              }`}
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
              {!compact ? (isFullscreen ? 'Salir PC' : 'Pantalla') : ''}
            </button>
            )}
            <button
              type="button"
              onClick={cerrarSesion}
              className={`flex items-center rounded-xl bg-white/15 font-black text-white ring-1 ring-white/20 hover:bg-white/20 ${
                compact
                  ? 'min-h-9 gap-1 px-2 text-xs'
                  : 'min-h-[46px] gap-2 px-3 text-sm'
              }`}
              title="Cerrar sesión o cambiar usuario"
            >
              <FaSignOutAlt />
              {!compact ? 'Salir' : ''}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`shrink-0 rounded-xl border p-2 text-xs shadow-card sm:text-sm ${
          isOnline ? 'border-e0e3-ok/35 bg-e0e3-ok/10 text-e0e3-ok' : 'border-e0e3-warn/40 bg-e0e3-warn/10 text-e0e3-warn'
        }`}
      >
        {isOnline
          ? `En linea${pendingSalesCount > 0 ? ` · Pendientes por sincronizar: ${pendingSalesCount}` : ' · Sin pendientes'}`
          : `Sin conexion · Ventas pendientes: ${pendingSalesCount}. Podes seguir facturando y se sincroniza al volver internet.`}
      </div>

      {catalogUsingSaved && catalogReady && (
        <div
          className="flex shrink-0 items-center justify-between gap-2 rounded-xl border border-e0e3-warn/30 bg-e0e3-warn/10 px-2.5 py-1.5 text-xs font-semibold text-e0e3-warn shadow-card sm:text-sm"
        >
          <span>Usando catálogo guardado{catalogCount > 0 ? ` · ${catalogCount} productos` : ''}</span>
          <button
            type="button"
            onClick={() => refreshCatalogoCajero({ force: true })}
            disabled={catalogRefreshing || !sucursalVenta}
            className="shrink-0 rounded-md border border-e0e3-warn/35 bg-black/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide hover:bg-black/30 disabled:opacity-60"
          >
            {catalogRefreshing ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      )}

      {sucursalesDisponibles?.length > 1 && (
        <div className="shrink-0 rounded-xl border border-white/[0.06] bg-e0e3-surface/95 p-2 shadow-card sm:p-2">
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-e0e3-muted">
            <FaStore className="text-e0e3-accentSoft" />
            Sucursal
          </div>
          <div className="flex flex-wrap gap-2">
            {sucursalesDisponibles.map((sucursal) => (
              <button
                key={sucursal.id}
                onClick={() => setSucursalVenta(sucursal.id)}
                className={`rounded-lg font-black px-3 py-2 text-xs sm:text-sm ${
                  sucursalVenta === sucursal.id
                    ? 'bg-e0e3-accent text-white shadow-[0_0_18px_-4px_rgba(30,144,255,0.65)]'
                    : 'border border-white/[0.08] bg-e0e3-surfaceHover text-e0e3-muted hover:border-white/20'
                }`}
              >
                {sucursal.nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      {cajaClientesHabilitado && bloqueClienteActualCajero()}

      {bloqueBusquedaPos({ landscape: false })}
        </>
        )}

      {bannerActualizacionApk}

      {landscapeTablet ? (
        <div className="cajero-pos-tablet-shell">
          {ultimaVenta && (
            <div className="mb-1 shrink-0 truncate rounded-lg border border-e0e3-ok/35 bg-e0e3-ok/10 px-2 py-0.5 text-center text-[10px] font-black text-e0e3-ok">
              Última venta #{String(ultimaVenta.id || '').slice(-12)} · {formatMoneda(ultimaVenta.total)} · vuelto {formatMoneda(ultimaVenta.vuelto)}
            </div>
          )}

          <div className="cajero-pos-main-grid">
            <section aria-label="Ticket y búsqueda" className="cajero-pos-left-pane gap-2">
              {cajaClientesHabilitado && bloqueClienteActualCajero({ compact: true })}
              {bloqueBusquedaPos({ landscape: true })}
              {mostrarResultados && (
                <div aria-label="Resultados de búsqueda" className="cajero-pos-results-pane scrollbar-thin">
                  {resultadosSolo}
                </div>
              )}
              {tarjetaCarritoLandscape}
            </section>
            {panelCheckoutPosLandscape}
          </div>
        </div>
      ) : (
        <div className="scrollbar-thin flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overscroll-y-contain pr-0.5">
          {resultadosSolo}
          {carritoCobroUltima}
        </div>
      )}

      <AppVersionDisplay variant="compact" className="shrink-0 pb-0.5" poll />

      </div>

      {mostrarClientes && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/75 backdrop-blur-sm">
          <div className="shrink-0 border-b border-white/[0.08] bg-gradient-to-r from-e0e3-shell via-[#16181e] to-e0e3-bg px-4 py-4 shadow-card">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xl font-black text-white sm:text-2xl">Seleccionar cliente</div>
                <div className="text-sm font-semibold text-e0e3-muted">
                  Predeterminado: Consumidor final
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMostrarClientes(false)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.1] bg-white/[0.08] text-white hover:bg-white/[0.14] active:scale-[0.98]"
                aria-label="Cerrar selector de cliente"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="scrollbar-thin flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto bg-e0e3-bg p-3 sm:p-4">
            <button
              type="button"
              onClick={seleccionarConsumidorFinal}
              className="w-full rounded-2xl border border-e0e3-accent/35 bg-e0e3-surface/95 p-4 text-left shadow-card ring-1 ring-e0e3-accent/15 hover:bg-e0e3-surfaceHover active:scale-[0.99]"
            >
              <div className="text-lg font-black text-white">Consumidor final</div>
              <div className="text-sm font-semibold text-e0e3-muted">Venta sin cliente asociado</div>
            </button>

            <input
              type="text"
              value={busquedaCliente}
              onChange={(event) => setBusquedaCliente(event.target.value)}
              placeholder="Buscar por nombre, teléfono, email o DNI/CUIT"
              className="w-full min-h-[52px] rounded-2xl border-2 border-e0e3-accent/45 bg-[#141416] px-4 py-3.5 text-base font-semibold text-white placeholder:text-e0e3-muted/75 shadow-inner focus:border-e0e3-accent focus:outline-none focus:ring-4 focus:ring-e0e3-accent/20"
              autoFocus
              autoComplete="off"
              inputMode="search"
            />

            {loadingClientes ? (
              <div className="rounded-xl border border-white/[0.08] bg-e0e3-surface/95 p-4 text-center text-sm font-semibold text-e0e3-muted shadow-card">
                Buscando clientes...
              </div>
            ) : (
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pb-2">
                {clientesEncontrados.map((cliente) => (
                  <button
                    key={cliente.id}
                    type="button"
                    onClick={() => seleccionarCliente(cliente)}
                    className="w-full rounded-2xl border border-white/[0.08] bg-e0e3-surface/95 p-4 text-left shadow-card hover:border-white/[0.14] hover:bg-e0e3-surfaceHover active:scale-[0.99]"
                  >
                    <div className="text-lg font-black text-white">{nombreCliente(cliente)}</div>
                    <div className="text-sm font-semibold text-e0e3-muted">
                      {cliente.telefono || cliente.email || cliente.dni_cuit || 'Sin datos de contacto'}
                    </div>
                    {Array.isArray(cliente.deudas) && cliente.deudas.length > 0 && (
                      <div className="mt-2 rounded-lg border border-e0e3-warn/35 bg-e0e3-warn/10 px-3 py-2 text-xs font-bold text-e0e3-warn">
                        Tiene comprobantes pendientes
                      </div>
                    )}
                  </button>
                ))}
                {!loadingClientes && clientesEncontrados.length === 0 && (
                  <div className="rounded-xl border border-white/[0.08] bg-e0e3-surface/95 p-4 text-center text-sm font-semibold text-e0e3-muted shadow-card">
                    No se encontraron clientes.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {modalAlertaDeudaInicial && clienteCajeroConId && saldoOficialDeuda > 0 && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/75 p-4">
          <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-e0e3-surface p-5 text-white shadow-xl">
            <h3 className="mb-1 text-lg font-black text-amber-300">⚠ Atención</h3>
            <p className="mb-3 text-base font-black text-white">{nombreCliente(clienteSeleccionado)}</p>
            <p className="mb-4 text-sm font-semibold text-e0e3-muted">
              Posee deuda pendiente.
            </p>
            <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">
                Saldo actual
              </div>
              <div className="text-3xl font-black text-amber-300">
                {formatMoneda(saldoOficialDeuda)}
              </div>
            </div>
            <p className="mb-4 text-sm font-semibold text-e0e3-muted">¿Qué desea hacer?</p>
            <div className="flex flex-col gap-2">
              {cajaPagoDeudasHabilitado && (
                <button
                  type="button"
                  onClick={iniciarCobroDeudaCajero}
                  className="min-h-12 rounded-xl bg-emerald-600 py-3 text-base font-black text-white hover:bg-emerald-500 active:scale-[0.99]"
                >
                  Cobrar deuda
                </button>
              )}
              <button
                type="button"
                onClick={continuarVentaConDeuda}
                className="min-h-12 rounded-xl border border-e0e3-accent/45 bg-e0e3-accent/15 py-3 text-base font-black text-e0e3-accentSoft hover:bg-e0e3-accent/25 active:scale-[0.99]"
              >
                Continuar venta
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarDeudaCliente && deudaTotalCliente > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[calc(var(--app-vh-unit,1vh)*90)] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xl font-black text-e0e3-foreground">Cliente con deuda</div>
                <div className="text-sm font-semibold text-e0e3-muted">{nombreCliente(clienteSeleccionado)}</div>
              </div>
              <button
                type="button"
                onClick={() => setMostrarDeudaCliente(false)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] text-e0e3-muted"
              >
                <FaTimes />
              </button>
            </div>

            <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-amber-900">
              <div className="text-sm font-semibold">Deuda total</div>
              <div className="text-3xl font-black">{formatMoneda(deudaTotalCliente)}</div>
            </div>

            <div className="mt-4 space-y-2">
              {filasComprobantesPagables.map((fila) => (
                <div key={fila.id} className="rounded-2xl border border-gray-100 bg-white/[0.04] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-black text-e0e3-foreground">
                        Comprobante {fila.numero || fila.id}
                      </div>
                      <div className="text-xs font-semibold text-e0e3-muted">
                        {formatearFecha(fila.fecha)} · {fila.diasAtraso || 0} días
                      </div>
                    </div>
                    <div className="text-right text-lg font-black text-amber-700">
                      {formatMoneda(fila.saldo)}
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {cajaVerComprobanteHabilitado && (
                      <button
                        type="button"
                        onClick={() => setComprobanteDeuda(fila)}
                        className="rounded-xl bg-white px-3 py-2 text-sm font-black text-e0e3-foreground"
                      >
                        Ver
                      </button>
                    )}
                    {cajaPagoDeudasHabilitado && (
                      <button
                        type="button"
                        onClick={() => {
                          setComprobanteDeuda(fila);
                          setMontoPagoDeuda(String(fila.saldo || ''));
                        }}
                        className="rounded-xl bg-green-600 px-3 py-2 text-sm font-black text-white"
                      >
                        Pagar
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {filasComprobantesPagables.length === 0 && deudaTotalCliente > 0 && (
                <p className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
                  {MENSAJE_SALDO_SIN_COMPROBANTES_CAJA}
                </p>
              )}
            </div>

            {comprobanteDeuda && (
              <div className="mt-4 rounded-2xl border border-blue-100 bg-e0e3-accent/10 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-black text-blue-900">
                      Comprobante {comprobanteDeuda.numero || comprobanteDeuda.id}
                    </div>
                    <div className="text-xs font-semibold text-blue-700">
                      Fecha {formatearFecha(comprobanteDeuda.fecha)} · Total {formatMoneda(comprobanteDeuda.total)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setComprobanteDeuda(null)}
                    className="text-blue-700"
                  >
                    <FaTimes />
                  </button>
                </div>

                {cajaPagoDeudasHabilitado && (
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={montoPagoDeuda}
                      onChange={(event) => setMontoPagoDeuda(event.target.value)}
                      className="w-full rounded-xl border border-blue-200 px-3 py-3 text-lg font-black focus:outline-none"
                      inputMode="decimal"
                    />
                    <select
                      value={medioPagoDeuda}
                      onChange={(event) => setMedioPagoDeuda(event.target.value)}
                      className="w-full rounded-xl border border-blue-200 px-3 py-3 font-bold focus:outline-none"
                    >
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta</option>
                      <option value="MercadoPago">{BILLETERA_VIRTUAL_LABEL}</option>
                    </select>
                    <button
                      type="button"
                      onClick={pagarComprobanteDeuda}
                      disabled={pagandoDeuda}
                      className="min-h-[50px] w-full rounded-xl bg-green-600 text-base font-black text-white disabled:bg-white/15"
                    >
                      {pagandoDeuda ? 'Registrando pago...' : 'Registrar pago'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setMostrarDeudaCliente(false)}
              className="mt-4 min-h-[52px] w-full rounded-2xl bg-white/[0.06] text-base font-black text-e0e3-foreground"
            >
              Ignorar y facturar nueva venta
            </button>
          </div>
        </div>
      )}

      {mostrarCarritoCompleto && (
        <div className="fixed inset-0 z-40 flex h-[100dvh] min-h-0 flex-col bg-white/[0.06]">
          <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-4 py-4 text-white shadow">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-2xl font-black">Carrito</div>
                <div className="text-sm font-semibold text-blue-100">
                  Editá cantidades o quitá productos
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-blue-100">Total</div>
                  <div className="text-3xl font-black">{formatMoneda(total)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setMostrarCarritoCompleto(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/20"
                  aria-label="Cerrar carrito"
                  title="Cerrar"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-3">
            {carrito.length === 0 ? (
              <div className="rounded-2xl bg-white p-8 text-center font-semibold text-e0e3-muted shadow">
                El carrito está vacío.
              </div>
            ) : (
              <div className="space-y-2">
                {carrito.map((item) => (
                  <div key={item.id} className="rounded-2xl bg-white p-3 shadow-sm">
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-base font-black text-e0e3-foreground">{item.nombre}</div>
                        <div className="text-xs font-semibold text-e0e3-muted">
                          {item.codigo || 'Sin código'} · {formatMoneda(item.precio)} c/u · Stock {item.stock_disponible}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => actualizarCantidad(item.id, 0)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-e0e3-danger"
                        aria-label={`Quitar ${item.nombre}`}
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => actualizarCantidad(item.id, item.cantidad - 1)}
                          className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.06] text-e0e3-foreground"
                        >
                          <FaMinus />
                        </button>
                        <div className="min-w-[54px] text-center text-2xl font-black text-e0e3-foreground">
                          {item.cantidad}
                        </div>
                        <button
                          type="button"
                          onClick={() => actualizarCantidad(item.id, item.cantidad + 1)}
                          disabled={false}
                          className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.06] text-e0e3-foreground disabled:opacity-40"
                        >
                          <FaPlus />
                        </button>
                      </div>
                      <div className="text-2xl font-black text-e0e3-foreground">
                        {formatMoneda(item.precio * item.cantidad)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-white/10 bg-white p-3 shadow-2xl">
            <div className="mb-3 flex items-center justify-between rounded-2xl bg-gray-900 px-4 py-3 text-white">
              <span className="text-lg font-black">Total</span>
              <span className="text-3xl font-black">{formatMoneda(total)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMostrarCarritoCompleto(false)}
                className="min-h-[58px] rounded-2xl bg-white/[0.06] text-lg font-black text-e0e3-foreground"
              >
                Volver
              </button>
              <button
                type="button"
                onClick={() => setMostrarCarritoCompleto(false)}
                disabled={carrito.length === 0}
                className="min-h-[58px] rounded-2xl bg-white/[0.12] text-lg font-black text-white hover:bg-white/[0.18] disabled:opacity-40"
              >
                Volver al cobro
              </button>
            </div>
          </div>
        </div>
      )}

      {ticketPendiente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl">
            <div className="mb-2 flex items-center gap-3 text-e0e3-foreground">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <FaCheck className="text-2xl" />
              </div>
              <div>
                <div className="text-xl font-black">Venta registrada</div>
                <div className="text-sm font-semibold text-e0e3-muted">
                  Total {formatMoneda(ticketPendiente.total)}
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-e0e3-muted">
              El carrito ya está vacío. Tocá «Siguiente venta» para escanear el próximo producto.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={cerrarPostVentaYContinuar}
                className="min-h-[52px] rounded-2xl bg-emerald-600 text-base font-black text-white hover:bg-emerald-500"
              >
                Siguiente venta
              </button>
              <button
                type="button"
                onClick={() => {
                  abrirTicketVenta(ticketPendiente, false);
                  setTicketPendiente(null);
                }}
                className="min-h-[48px] rounded-2xl border border-blue-200 bg-blue-50 text-sm font-black text-blue-800 hover:bg-blue-100"
              >
                Imprimir ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {ticketVentaPayload?.venta && (
        <TicketVenta
          venta={ticketVentaPayload.venta}
          thermalRollWidth="58mm"
          autoPrintAfterLoad={ticketVentaPayload.autoPrint}
          hidePdfButton
          onClose={() => {
            setTicketVentaPayload(null);
            enfocarBusquedaPOS();
          }}
        />
      )}

      <ProductoNoEncontradoModal
        isOpen={productoNoEncontrado.open}
        onClose={() => {
          setProductoNoEncontrado({ open: false, codigo: '' });
          enfocarBusquedaPOS();
        }}
        codigoInicial={productoNoEncontrado.codigo}
        sucursalId={sucursalVenta}
        onCreado={(c) => handleProductoRapidoCreado(c, { vender: false })}
        onCrearYVender={(c) => handleProductoRapidoCreado(c, { vender: true })}
      />

      <PesoManualVentaModal
        isOpen={pesoManualVenta.open}
        onClose={() => {
          setPesoManualVenta({ open: false, producto: null });
          enfocarBusquedaPOS();
        }}
        producto={pesoManualVenta.producto}
        posPoliticas={posPoliticas}
        onAgregar={agregarProductoPesoManual}
      />

      {mostrarModalCierreCaja && orgId && (
        <div className="fixed inset-0 z-[55] flex items-end justify-center bg-black/70 p-2 sm:items-center sm:p-4">
          <div className="cajero-cierre-modal w-full max-w-lg max-h-[92vh] overflow-y-auto overscroll-y-contain">
            <CierreDiarioPanel
              orgId={orgId}
              sucursalId={sucursalVenta}
              dark
              soloDiaActual={!canVerHistorialCierres(hasPermission)}
              umbralAdminAuth={posPoliticas.umbral_diferencia_cierre_admin}
              canCerrar={canCerrarCaja(hasPermission)}
              canCorregir={canCorregirCierreCaja(hasPermission)}
              onClose={() => setMostrarModalCierreCaja(false)}
            />
          </div>
        </div>
      )}

      {modalStockNegativo && (
        <div className="fixed inset-0 z-[56] flex items-center justify-center bg-black/75 p-4">
          <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-e0e3-surface p-4 text-white shadow-xl">
            <h3 className="mb-2 text-lg font-black text-amber-300">Stock insuficiente</h3>
            <p className="mb-3 text-sm text-e0e3-muted">
              Los siguientes productos quedarán con stock negativo:
            </p>
            <ul className="mb-4 max-h-32 overflow-y-auto text-sm">
              {lineasStockNegativo.map((item) => (
                <li key={item.id || item.producto_id} className="border-b border-white/5 py-1">
                  {item.nombre} · pedido {item.cantidad} · disponible {item.stock_disponible}
                </li>
              ))}
            </ul>
            <label className="mb-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={stockNegConfirmChecked}
                onChange={(e) => setStockNegConfirmChecked(e.target.checked)}
                className="mt-1 h-4 w-4 rounded accent-amber-500"
              />
              <span>Autorizar venta con stock negativo</span>
            </label>
            <input
              type="email"
              placeholder="Email administrador"
              value={stockNegAdminEmail}
              onChange={(e) => setStockNegAdminEmail(e.target.value)}
              className="mb-2 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm"
            />
            <input
              type="password"
              placeholder="Contraseña administrador"
              value={stockNegAdminPassword}
              onChange={(e) => setStockNegAdminPassword(e.target.value)}
              className="mb-2 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm"
            />
            <textarea
              value={stockNegMotivo}
              onChange={(e) => setStockNegMotivo(e.target.value)}
              placeholder="Motivo (opcional)"
              rows={2}
              className="mb-3 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setModalStockNegativo(false);
                  pendingCheckoutRef.current = null;
                }}
                className="flex-1 rounded-xl border border-white/15 py-2 font-bold"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarVentaStockNegativo}
                className="flex-1 rounded-xl bg-amber-600 py-2 font-bold text-white hover:bg-amber-500"
              >
                Autorizar y cobrar
              </button>
            </div>
          </div>
        </div>
      )}

      {modalCreditoExcedido && (
        <div className="fixed inset-0 z-[56] flex items-center justify-center bg-black/75 p-4">
          <div className="w-full max-w-md rounded-xl border border-red-500/40 bg-e0e3-surface p-4 text-white shadow-xl">
            <h3 className="mb-3 text-lg font-black text-red-300">⚠ Crédito excedido</h3>
            <div className="mb-4 space-y-2 text-sm">
              <p>
                <span className="text-e0e3-muted">Cliente:</span>{' '}
                <span className="font-bold">{modalCreditoExcedido.clienteNombre}</span>
              </p>
              <p>
                <span className="text-e0e3-muted">Límite autorizado:</span>{' '}
                <span className="font-bold">{formatMoneda(modalCreditoExcedido.limite)}</span>
              </p>
              <p>
                <span className="text-e0e3-muted">Deuda actual:</span>{' '}
                <span className="font-bold">{formatMoneda(modalCreditoExcedido.deudaActual)}</span>
              </p>
              <p>
                <span className="text-e0e3-muted">Nueva deuda:</span>{' '}
                <span className="font-bold">{formatMoneda(modalCreditoExcedido.saldoNuevaVenta)}</span>
              </p>
              <p className="border-t border-white/10 pt-2">
                <span className="text-e0e3-muted">Total:</span>{' '}
                <span className="text-lg font-black text-red-300">
                  {formatMoneda(modalCreditoExcedido.totalProyectado)}
                </span>
              </p>
            </div>
            <p className="mb-4 text-xs text-e0e3-muted">
              La venta supera el límite de cuenta corriente. Podés continuar bajo tu responsabilidad.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setModalCreditoExcedido(null);
                  pendingCheckoutRef.current = null;
                }}
                className="flex-1 rounded-xl border border-white/15 py-2 font-bold"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarVentaCreditoExcedido}
                className="flex-1 rounded-xl bg-red-600 py-2 font-bold text-white hover:bg-red-500"
              >
                Continuar con advertencia
              </button>
            </div>
          </div>
        </div>
      )}
      <RegistrarPagoDialog
        isOpen={pagoDeudaDialog.open}
        onClose={() => setPagoDeudaDialog({ open: false, venta: null })}
        venta={pagoDeudaDialog.venta}
        onPagoRegistrado={registrarPagoDeudaCajero}
      />
      <ActivarProductoMasterModal
        isOpen={activacionMaster.open}
        producto={activacionMaster.producto}
        sucursalId={sucursalVenta || sucursalSeleccionada?.id}
        orgId={orgId}
        variant="pos"
        onFocusScan={enfocarBusquedaPOS}
        onClose={() => setActivacionMaster({ open: false, producto: null })}
        onSuccess={handleActivacionMasterSuccess}
      />
    </>
  );
};

export default MobilePuntoVenta;